import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import Anthropic from '@anthropic-ai/sdk';
import { ScheduleCalendar, MATCHING_PROMPT_EXAMPLES, RISK_PROMPT_EXAMPLES, GROUP_PROMPT_EXAMPLES } from '@fulcrum/schedule-embedded';
import type {
  ScheduleEvent,
  ScheduleConfig,
  ScheduleCallbacks,
  ConnectionStatus,
  EventMovedPayload,
  EventResizedPayload,
  ViewType,
  SlotDuration,
  Resource,
  WorkingHours,
  DayWorkingHours,
  HeaderDateFormats,
  TimeFormat,
  SecondaryTimezone,
  ColumnHeaderFormats,
  ToolbarButtonConfig,
  ToolbarButtonsConfig,
  ResourcePanelConfig,
  HolidayDefinition,
  ZoomConfig,
  SchedulingQueueConfig,
  ScheduleValidation,
  ValidationError,
  PropertyItem,
  PropertiesPanelConfig,
  TvModeConfig,
  AiChatPanelConfig,
  AiMessageContext,
  AiResponse,
  AiCreateEventRequest,
  ScreenerGateConfig,
  SlotSelectedPayload,
  ScreenerCompletePayload,
  ActiveScreener,
  AcuitySeverity,
  AcuitySlotConfig,
  AcuityReservation,
  AcuityCommandPayload,
  AcuitySlotClaimedPayload,
  AcuitySlotsRequestPayload,
  ReleaseWarningPayload,
  ProviderMatchingConfig,
  PatientContext,
  MatchingRequestPayload,
  MatchingResultsPayload,
  MatchingCommandPayload,
  ProviderMatch,
  ProviderSelectedPayload,
  WaitlistConfig,
  WaitlistEntry,
  WaitlistStatePayload,
  SlotOpenedPayload,
  BackfillMatchPayload,
  BackfillNoMatchPayload,
  BackfillOutcomePayload,
  WaitlistCommandPayload,
  NoShowRiskConfig,
  RiskScoresRequestPayload,
  ReminderDuePayload,
  AppointmentRiskScore,
  RiskLevel,
  RiskCommandPayload,
  // Group Therapy
  GroupTherapyConfig,
  GroupSession,
  GroupEnrollment,
  GroupConflict,
  GroupSessionsRequestPayload,
  GroupEnrollRequestPayload,
  GroupEnrollOutcomePayload,
  GroupAttendanceRequestPayload,
  GroupAttendancePayload,
  GroupConflictCheckPayload,
  GroupConflictResultPayload,
  GroupCommandPayload,
  Client,
} from '@fulcrum/schedule-embedded';
import { generateEvents, getDefaultConfig, RESOURCES, US_HOLIDAYS_2026, BACKGROUND_EVENTS, CUSTOM_ATTRIBUTE_DEFS, GROUP_EVENT_IDS, generateGroupTherapyEvents, DEMO_CLIENTS } from './mockData';
import { utcToScheduleLocal, scheduleLocalToUtc } from '@fulcrum/schedule-embedded';
import { BH_PROVIDERS, BH_CUSTOM_ATTRS, generateBhEvents, getBhConfig } from './industryData';
import { useSseSchedule } from './hooks/useSseSchedule';
import './App.css';

// ─── Intake Screener Gate — static screener definitions ───────────────────

const PHQ9_SCREENER: ActiveScreener = {
  id: 'PHQ-9',
  title: 'PHQ-9 Depression Screener',
  instructions: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
  acuityThresholds: { low: 4, moderate: 9, high: 19 },
  questions: [
    { id: 'phq1', text: 'Little interest or pleasure in doing things', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq2', text: 'Feeling down, depressed, or hopeless', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq3', text: 'Trouble falling or staying asleep, or sleeping too much', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq4', text: 'Feeling tired or having little energy', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq5', text: 'Poor appetite or overeating', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq6', text: 'Feeling bad about yourself — or that you are a failure', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq7', text: 'Trouble concentrating on things, such as reading or watching television', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq8', text: 'Moving or speaking so slowly that others could notice — or being fidgety', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'phq9', text: 'Thoughts that you would be better off dead, or of hurting yourself', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
  ],
};

const GAD7_SCREENER: ActiveScreener = {
  id: 'GAD-7',
  title: 'GAD-7 Anxiety Screener',
  instructions: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
  acuityThresholds: { low: 4, moderate: 9, high: 14 },
  questions: [
    { id: 'gad1', text: 'Feeling nervous, anxious, or on edge', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'gad2', text: 'Not being able to stop or control worrying', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'gad3', text: 'Worrying too much about different things', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'gad4', text: 'Trouble relaxing', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'gad5', text: 'Being so restless that it is hard to sit still', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'gad6', text: 'Becoming easily annoyed or irritable', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
    { id: 'gad7', text: 'Feeling afraid, as if something awful might happen', type: 'likert', options: [{ label: 'Not at all', value: 0 }, { label: 'Several days', value: 1 }, { label: 'More than half the days', value: 2 }, { label: 'Nearly every day', value: 3 }] },
  ],
};

const CSSRS_SCREENER: ActiveScreener = {
  id: 'C-SSRS',
  title: 'Columbia Suicide Severity Rating Scale',
  instructions: 'Please answer each question honestly. Your responses are confidential and help us ensure you receive the right level of care.',
  acuityThresholds: { low: 0, moderate: 2, high: 3 },
  questions: [
    { id: 'cssrs1', text: 'Have you wished you were dead or wished you could go to sleep and not wake up?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'cssrs2', text: 'Have you had any actual thoughts of killing yourself?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'cssrs3', text: 'Have you been thinking about how you might do this?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'cssrs4', text: 'Have you had any intention of acting on these thoughts?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'cssrs5', text: 'Have you started to work out or worked out the details of how to end your life? Do you intend to carry out this plan?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'cssrs6', text: 'Have you ever done anything, started to do anything, or prepared to do anything to end your life?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
  ],
};

const AUDIT_C_SCREENER: ActiveScreener = {
  id: 'AUDIT-C',
  title: 'AUDIT-C Alcohol Use Screener',
  instructions: 'Please answer the following questions about your alcohol use over the past year.',
  acuityThresholds: { low: 2, moderate: 5, high: 8 },
  questions: [
    {
      id: 'auditc1',
      text: 'How often do you have a drink containing alcohol?',
      type: 'likert',
      options: [
        { label: 'Never', value: 0 },
        { label: 'Monthly or less', value: 1 },
        { label: '2–4 times per month', value: 2 },
        { label: '2–3 times per week', value: 3 },
        { label: '4+ times per week', value: 4 },
      ],
    },
    {
      id: 'auditc2',
      text: 'How many standard drinks do you have on a typical day when you are drinking?',
      type: 'likert',
      options: [
        { label: '1–2', value: 0 },
        { label: '3–4', value: 1 },
        { label: '5–6', value: 2 },
        { label: '7–9', value: 3 },
        { label: '10 or more', value: 4 },
      ],
    },
    {
      id: 'auditc3',
      text: 'How often do you have six or more drinks on one occasion?',
      type: 'likert',
      options: [
        { label: 'Never', value: 0 },
        { label: 'Less than monthly', value: 1 },
        { label: 'Monthly', value: 2 },
        { label: 'Weekly', value: 3 },
        { label: 'Daily or almost daily', value: 4 },
      ],
    },
  ],
};

const DAST10_SCREENER: ActiveScreener = {
  id: 'DAST-10',
  title: 'DAST-10 Drug Abuse Screening Test',
  instructions: 'The following questions concern information about your possible involvement with drugs, not including alcohol or tobacco. Please answer each question YES or NO.',
  acuityThresholds: { low: 2, moderate: 5, high: 8 },
  questions: [
    { id: 'dast1', text: 'Have you used drugs other than those required for medical reasons?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast2', text: 'Do you abuse more than one drug at a time?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast3', text: 'Are you always able to stop using drugs when you want to?', type: 'likert', options: [{ label: 'No', value: 1 }, { label: 'Yes', value: 0 }] },
    { id: 'dast4', text: 'Have you had blackouts or flashbacks as a result of drug use?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast5', text: 'Do you ever feel bad or guilty about your drug use?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast6', text: 'Does your spouse or family complain about your involvement with drugs?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast7', text: 'Have you neglected your family because of your drug use?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast8', text: 'Have you engaged in illegal activities in order to obtain drugs?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast9', text: 'Have you experienced withdrawal symptoms when you stopped taking drugs?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
    { id: 'dast10', text: 'Have you had medical problems as a result of your drug use (e.g. memory loss, hepatitis, convulsions, bleeding)?', type: 'likert', options: [{ label: 'No', value: 0 }, { label: 'Yes', value: 1 }] },
  ],
};

// ─── Acuity Slot Reservation — demo reservation generator ────────────────
// Generates 3 sample reservations anchored to the first visible day of the range.
// Urgent is hidden from general; high is visible but locked; routine shows a
// countdown chip (releaseAt = 25 min from now so the chip fires immediately).

function generateDemoReservations(
  rangeStart: string,
  _rangeEnd: string,
  resourceIds: string[],
  routineReleaseAt: string,
): AcuityReservation[] {
  // Assign reservations across the first two available resources if possible.
  const r0 = resourceIds[0] ?? 'default';
  const r1 = resourceIds[1] ?? r0;
  const r2 = resourceIds[2] ?? r0;

  // Use LOCAL date construction so hours 9/11/14 are in the browser's local time,
  // not UTC. UTC construction shifts slots outside business hours for US timezones.
  const anchorDate = new Date(rangeStart);
  const slot = (hour: number, minutes = 30): { start: string; end: string } => {
    const start = new Date(
      anchorDate.getFullYear(),
      anchorDate.getMonth(),
      anchorDate.getDate(),
      hour, 0, 0, 0,
    );
    const end = new Date(start.getTime() + minutes * 60_000);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  return [
    {
      id: 'demo-urgent-1',
      resourceId: r0,
      ...slot(9),
      acuityLevel: 'urgent',
      label: 'Crisis follow-up',
      visibleToGeneral: false,
    },
    {
      id: 'demo-high-1',
      resourceId: r1,
      ...slot(11),
      acuityLevel: 'high',
      label: 'Post-discharge',
      visibleToGeneral: true,
      lockReason: 'Reserved for post-discharge patients — contact scheduling to book',
    },
    {
      id: 'demo-routine-1',
      resourceId: r2,
      ...slot(14),
      acuityLevel: 'routine',
      label: 'Urgent new intake',
      visibleToGeneral: true,
      releaseAt: routineReleaseAt,
    },
  ];
}

// ─── Provider Matching — demo matching engine ─────────────────────────────
// The host owns all matching logic. This demo scores providers against
// hard-coded specialty/language/modality/insurance metadata.

interface DemoProviderProfile {
  resourceId: string;
  specialties: string[];
  modalities: string[];
  languages: string[];   // BCP 47
  acceptedInsurance: string[];
  offersIn_person: boolean;
  offersTelehealth: boolean;
  ageGroups: string[];
  gender: 'male' | 'female' | 'nonbinary';
}

// Maps RESOURCES ids to clinical profiles for the demo.
// IDs taken from the mock data to keep things consistent.
const DEMO_PROVIDER_PROFILES: DemoProviderProfile[] = [
  { resourceId: 'alice',   specialties: ['trauma', 'adolescent'],        modalities: ['CBT', 'EMDR'],     languages: ['en', 'es'],    acceptedInsurance: ['Aetna', 'Cigna'],      offersIn_person: true,  offersTelehealth: true,  ageGroups: ['adolescent', 'adult'], gender: 'female' },
  { resourceId: 'bob',     specialties: ['addiction', 'adult'],          modalities: ['DBT', 'ACT'],      languages: ['en'],          acceptedInsurance: ['Cigna', 'UHC'],        offersIn_person: true,  offersTelehealth: false, ageGroups: ['adult', 'older_adult'], gender: 'male' },
  { resourceId: 'carol',   specialties: ['trauma', 'PTSD'],              modalities: ['EMDR', 'CBT'],     languages: ['en', 'zh-TW'], acceptedInsurance: ['BlueCross'],           offersIn_person: false, offersTelehealth: true,  ageGroups: ['adult'], gender: 'female' },
  { resourceId: 'dave',    specialties: ['adolescent', 'family'],        modalities: ['CBT', 'ACT'],      languages: ['en'],          acceptedInsurance: ['Aetna', 'Medicaid'],   offersIn_person: true,  offersTelehealth: true,  ageGroups: ['child', 'adolescent'], gender: 'male' },
  { resourceId: 'eve',     specialties: ['depression', 'anxiety'],       modalities: ['CBT', 'DBT'],      languages: ['en', 'es'],    acceptedInsurance: ['Cigna', 'BlueCross'],  offersIn_person: true,  offersTelehealth: true,  ageGroups: ['adult', 'older_adult'], gender: 'female' },
  { resourceId: 'frank',   specialties: ['trauma', 'addiction', 'PTSD'], modalities: ['DBT', 'EMDR'],     languages: ['en'],          acceptedInsurance: ['UHC', 'Aetna'],        offersIn_person: true,  offersTelehealth: false, ageGroups: ['adult'], gender: 'male' },
  { resourceId: 'grace',   specialties: ['child', 'adolescent'],         modalities: ['play', 'CBT'],     languages: ['en', 'es'],    acceptedInsurance: ['Medicaid', 'Aetna'],   offersIn_person: true,  offersTelehealth: false, ageGroups: ['child', 'adolescent'], gender: 'female' },
  { resourceId: 'henry',   specialties: ['OCD', 'anxiety'],              modalities: ['CBT', 'ERP'],      languages: ['en', 'zh-TW'], acceptedInsurance: ['BlueCross', 'Cigna'],  offersIn_person: true,  offersTelehealth: true,  ageGroups: ['adult'], gender: 'male' },
  { resourceId: 'iris',    specialties: ['trauma', 'women'],             modalities: ['EMDR', 'CBT'],     languages: ['en', 'es'],    acceptedInsurance: ['Aetna', 'UHC'],        offersIn_person: true,  offersTelehealth: true,  ageGroups: ['adult'], gender: 'female' },
  { resourceId: 'jack',    specialties: ['couples', 'family'],           modalities: ['EFT', 'CBT'],      languages: ['en'],          acceptedInsurance: ['Cigna', 'BlueCross'],  offersIn_person: true,  offersTelehealth: true,  ageGroups: ['adult'], gender: 'male' },
];

function scoreProvider(profile: DemoProviderProfile, ctx: PatientContext): ProviderMatch {
  const criteria: NonNullable<PatientContext['preferences']> = ctx.preferences ?? {};
  const matched: string[] = [];
  const unmatched: string[] = [];

  // Specialty
  if (criteria.specialty?.length) {
    const hit = criteria.specialty.some((s) => profile.specialties.includes(s));
    hit ? matched.push(criteria.specialty[0]!) : unmatched.push(`Specialty: ${criteria.specialty.join(', ')}`);
  }
  // Modality
  if (criteria.modality?.length) {
    const hit = criteria.modality.some((m) => profile.modalities.includes(m));
    hit ? matched.push(criteria.modality[0]!) : unmatched.push(`Modality: ${criteria.modality.join(', ')}`);
  }
  // Language
  if (criteria.language?.length) {
    const hit = criteria.language.some((l) => profile.languages.includes(l));
    hit ? matched.push(criteria.language.map((l) => l === 'es' ? 'Spanish' : l === 'zh-TW' ? 'Mandarin' : l).join(', ')) : unmatched.push(`Language: ${criteria.language.join(', ')}`);
  }
  // Insurance
  if (criteria.insuranceId) {
    const hit = profile.acceptedInsurance.includes(criteria.insuranceId);
    hit ? matched.push(`In-network: ${criteria.insuranceId}`) : unmatched.push(`${criteria.insuranceId} not accepted`);
  }
  // Location
  if (criteria.locationPreference && criteria.locationPreference !== 'either') {
    const wantsTelehealth = criteria.locationPreference === 'telehealth';
    const available = wantsTelehealth ? profile.offersTelehealth : profile.offersIn_person;
    available ? matched.push(wantsTelehealth ? 'Telehealth' : 'In-person') : unmatched.push(`${wantsTelehealth ? 'Telehealth' : 'In-person'} not available`);
  }
  // Age group
  if (criteria.ageGroupPreference) {
    const hit = profile.ageGroups.includes(criteria.ageGroupPreference);
    hit ? matched.push(criteria.ageGroupPreference) : unmatched.push(`Age group: ${criteria.ageGroupPreference}`);
  }
  // Gender preference
  if (criteria.genderPreference && criteria.genderPreference !== 'no_preference') {
    const hit = profile.gender === criteria.genderPreference;
    hit ? matched.push(`${criteria.genderPreference} provider`) : unmatched.push(`Gender preference: ${criteria.genderPreference}`);
  }

  const total = matched.length + unmatched.length;
  const score = total === 0 ? 50 : Math.round((matched.length / total) * 100);
  const matchLevel: 'strong' | 'partial' | 'weak' =
    score >= 80 ? 'strong' : score >= 50 ? 'partial' : 'weak';

  return {
    resourceId: profile.resourceId,
    matchScore: score,
    matchLevel,
    matchedCriteria: matched,
    unmatchedCriteria: unmatched,
    badgeLabel: matchLevel === 'strong' ? 'Best match' : undefined,
  };
}

function runDemoMatching(
  ctx: PatientContext,
  resourceIds: string[],
): MatchingResultsPayload {
  const MIN_SCORE = 30;
  const scored: ProviderMatch[] = [];
  const unmatched: string[] = [];

  for (const id of resourceIds) {
    const profile = DEMO_PROVIDER_PROFILES.find((p) => p.resourceId === id);
    if (!profile) { unmatched.push(id); continue; }
    const result = scoreProvider(profile, ctx);
    if (result.matchScore >= MIN_SCORE) {
      scored.push(result);
    } else {
      unmatched.push(id);
    }
  }

  scored.sort((a, b) => b.matchScore - a.matchScore);
  return {
    patientId: ctx.patientId,
    matches: scored,
    unmatchedResourceIds: unmatched,
    matchSummary: `${scored.length} of ${resourceIds.length} providers match`,
  };
}

// ─── Business-case event icon ──────────────────────────────────────────────
// Renders a priority badge in the bottom-right corner of each event block.
// The host derives it from the event's customAttributes — the embedded
// component knows nothing about priority; it just calls the function.

const PRIORITY_ICON: Record<string, { symbol: string; color: string; title: string }> = {
  critical: { symbol: '🔴', color: '#ef4444', title: 'Critical priority' },
  high:     { symbol: '🟠', color: '#f97316', title: 'High priority' },
  medium:   { symbol: '🟡', color: '#eab308', title: 'Medium priority' },
  low:      { symbol: '🔵', color: '#3b82f6', title: 'Low priority' },
};

function eventPriorityIcon(event: ScheduleEvent): React.ReactNode {
  const attr = event.customAttributes?.find((a) => a.key === 'priority');
  if (!attr || typeof attr.value !== 'string') return null;
  const cfg = PRIORITY_ICON[attr.value];
  if (!cfg) return null;
  return (
    <span
      title={cfg.title}
      aria-label={cfg.title}
      style={{ fontSize: 10, lineHeight: 1 }}
    >
      {cfg.symbol}
    </span>
  );
}

// ─── Claude AI integration ─────────────────────────────────────────────────

const CLAUDE_MODEL = 'claude-haiku-4-5-20251001';

const SCHEDULE_AI_TOOL = {
  name: 'apply_schedule_change',
  description: 'Apply the requested display changes to the schedule calendar.',
  input_schema: {
    type: 'object' as const,
    required: ['reply'],
    properties: {
      reply: {
        type: 'string',
        description: 'Friendly one-to-two sentence confirmation shown to the user in the chat bubble.',
      },
      configPatch: {
        type: 'object',
        description: 'Partial ScheduleConfig — include only fields that should change from their current values.',
        properties: {
          view: {
            type: 'string',
            enum: ['day', 'weekdays', 'fullWeek', 'month', 'agenda'],
            description: 'day=single day, weekdays=Mon-Fri, fullWeek=Mon-Sun, month=monthly, agenda=event list',
          },
          timeRange: {
            type: 'object',
            properties: {
              start: { type: 'string', description: 'HH:MM 24-hour start time, e.g. "08:00"' },
              end:   { type: 'string', description: 'HH:MM 24-hour end time.  Use "00:00" for midnight / full-day.' },
            },
            required: ['start', 'end'],
          },
          agendaDays: {
            type: 'integer',
            description: 'Days the agenda view covers. Default 30. Use 35 for 5 weeks (5 of a given weekday).',
          },
          slotDuration: {
            type: 'integer',
            description: 'Minutes per time slot: 5, 10, 15, 20, 30, 45, 60, 90, or 120.',
          },
          timezone: {
            type: 'string',
            description: 'Primary IANA timezone for the schedule, e.g. "America/Chicago". All times are displayed in this zone.',
          },
          secondaryTimezones: {
            type: 'array',
            description: 'Additional time columns shown to the LEFT of the primary column. Empty array [] removes all secondary columns.',
            items: {
              type: 'object',
              required: ['timezone'],
              properties: {
                timezone: { type: 'string', description: 'IANA timezone string, e.g. "America/New_York"' },
                label:    { type: 'string', description: 'Short column label, e.g. "NYC" or "London". Omit to use the DST abbreviation.' },
              },
            },
          },
          showRightTimeColumn: {
            type: 'boolean',
            description: 'When true, mirrors the time column on the RIGHT edge of the grid. Default false.',
          },
          tvMode: {
            description: 'TV / display-board mode. Set to { enabled: true } to turn on, null to turn off.',
            oneOf: [
              {
                type: 'object',
                required: ['enabled'],
                properties: {
                  enabled:          { type: 'boolean' },
                  autoRotateMs:     { type: 'integer', description: 'Milliseconds between page rotations. Default 15000 (15 s).' },
                  resourcesPerPage: { type: 'integer', description: 'Providers shown per page in TV mode. Default 4.' },
                },
              },
              { type: 'null' },
            ],
          },
          resourcePanel: {
            description: 'Provider search panel to the LEFT of the schedule grid. Set to an object to enable, null to disable.',
            oneOf: [
              {
                type: 'object',
                properties: {
                  label:        { type: 'string' },
                  defaultWidth: { type: 'integer' },
                },
              },
              { type: 'null' },
            ],
          },
          schedulingQueue: {
            description: 'Scheduling queue panel to the RIGHT of the schedule grid. Set to an object to enable, null to disable.',
            oneOf: [
              {
                type: 'object',
                properties: {
                  label:        { type: 'string' },
                  defaultWidth: { type: 'integer' },
                },
              },
              { type: 'null' },
            ],
          },
          propertiesPanel: {
            description: 'Event properties panel to the RIGHT of the schedule grid. Set to an object to enable, null to disable.',
            oneOf: [
              {
                type: 'object',
                properties: {
                  label:        { type: 'string' },
                  defaultWidth: { type: 'integer' },
                },
              },
              { type: 'null' },
            ],
          },
        },
      },
      navigateTo: {
        type: 'string',
        description: 'YYYY-MM-DD date to jump the calendar to. Compute from today\'s date when the user says "next Friday" etc.',
      },
      resourceFilter: {
        type: 'array',
        items: { type: 'string' },
        description: 'Resource IDs to show. Empty array = show all configured resources. Omit = no change to current filter.',
      },
      createEvent: {
        type: 'object',
        description: 'Open the event creation form pre-filled with the collected event details.',
        required: ['resourceId', 'date', 'startTime'],
        properties: {
          resourceId: { type: 'string', description: 'Resource (provider) ID from the available resources list.' },
          date: { type: 'string', description: 'YYYY-MM-DD event date in the schedule\'s primary timezone.' },
          startTime: { type: 'string', description: 'HH:MM 24-hour start time in the schedule\'s primary timezone.' },
          durationMinutes: { type: 'integer', description: 'Duration in minutes. Omit to use the default slot duration.' },
          title: { type: 'string', description: 'Event title. Set to the primary subject (e.g. patient or client name) when known.' },
          customAttributes: {
            type: 'array',
            description: 'Custom field values collected from the conversation.',
            items: {
              type: 'object',
              required: ['key', 'value'],
              properties: {
                key:   { type: 'string', description: 'Custom attribute key as defined in the schedule config.' },
                value: { type: 'string', description: 'Value for this attribute.' },
              },
            },
          },
        },
      },
      acuityCommand: {
        type: 'object',
        description: 'Issue an acuity slot reservation command when the user asks to hold, release, or query reserved slots. Only set when acuity slots are enabled.',
        required: ['intent', 'raw'],
        properties: {
          intent: { type: 'string', enum: ['RESERVE', 'RELEASE', 'QUERY'], description: 'What the user wants to do.' },
          resourceId: { type: 'string', description: 'Resource (provider) ID the command applies to.' },
          date: { type: 'string', description: 'YYYY-MM-DD target date for the reservation.' },
          count: { type: 'integer', description: 'Number of slots to reserve (RESERVE intent).' },
          acuityLevel: { type: 'string', enum: ['urgent', 'high', 'routine'], description: 'Acuity level of the slots to reserve.' },
          label: { type: 'string', description: 'Display label for the reserved slot, e.g. "Post-discharge" or "Crisis follow-up".' },
          releaseAt: { type: 'string', description: 'ISO 8601 datetime when the slot auto-releases if unfilled.' },
          raw: { type: 'string', description: 'Original natural language input verbatim, for the host audit log.' },
        },
      },
      matchingCommand: {
        type: 'object',
        description: 'Filter or rank the provider list by clinical matching criteria. Only set when provider matching is enabled.',
        required: ['intent', 'raw'],
        properties: {
          intent: { type: 'string', enum: ['FILTER', 'RANK', 'CLEAR', 'QUERY'], description: 'FILTER=filter list, RANK=re-rank, CLEAR=remove filters, QUERY=answer about who matches.' },
          criteria: {
            type: 'object',
            description: 'Matching criteria — include only fields the user specified.',
            properties: {
              specialty:          { type: 'array', items: { type: 'string' }, description: 'e.g. ["trauma","adolescent"]' },
              modality:           { type: 'array', items: { type: 'string' }, description: 'e.g. ["CBT","DBT","EMDR"]' },
              language:           { type: 'array', items: { type: 'string' }, description: 'BCP 47: ["en","es","zh-TW"]' },
              genderPreference:   { type: 'string', enum: ['male','female','nonbinary','no_preference'] },
              ageGroupPreference: { type: 'string', enum: ['child','adolescent','adult','older_adult'] },
              insuranceId:        { type: 'string', description: 'Insurance plan e.g. "Cigna"' },
              locationPreference: { type: 'string', enum: ['in_person','telehealth','either'] },
            },
          },
          raw: { type: 'string', description: 'Verbatim user message, for the host audit log.' },
        },
      },
      riskCommand: {
        type: 'object',
        description: 'Issue a no-show risk command when the user asks about risk levels, unconfirmed appointments, outreach, or reminders. Only set when no-show risk is enabled.',
        required: ['intent', 'raw'],
        properties: {
          intent: { type: 'string', enum: ['QUERY', 'SUPPRESS', 'FLAG_OUTREACH', 'RESEND', 'OVERRIDE_SCHEDULE'], description: 'QUERY=answer about risk/confirmations, SUPPRESS=stop reminders, FLAG_OUTREACH=flag for manual outreach, RESEND=retrigger reminder.' },
          appointmentId: { type: 'string', description: 'Specific appointment ID the command targets.' },
          resourceId: { type: 'string', description: 'Resource (provider) ID to scope the command to.' },
          dateRange: {
            type: 'object',
            description: 'Date range to scope a QUERY command.',
            required: ['start', 'end'],
            properties: {
              start: { type: 'string', description: 'ISO 8601 start datetime.' },
              end:   { type: 'string', description: 'ISO 8601 end datetime.' },
            },
          },
          raw: { type: 'string', description: 'Verbatim user message, for the host audit log.' },
        },
      },
    },
  },
};

interface AiPanelState {
  tvModeEnabled: boolean;
  showResourcePanel: boolean;
  showSchedulingQueue: boolean;
  showPropertiesPanel: boolean;
  acuityEnabled: boolean;
  matchingEnabled: boolean;
  noShowRiskEnabled: boolean;
  riskScores: AppointmentRiskScore[];
}

function buildAiSystemPrompt(
  config: ScheduleConfig,
  allResources: Resource[],
  panels: AiPanelState,
): string {
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const todayIso = today.toISOString().split('T')[0];

  const configSummary = {
    view: config.view,
    timeRange: config.timeRange,
    timezone: config.timezone,
    slotDuration: config.slotDuration,
    agendaDays: config.agendaDays ?? 30,
    resourceGrouping: config.resourceGrouping ?? 'byDate',
  };

  const resourceList = allResources.map((r) => ({ id: r.id, name: r.name }));

  const secondaryZones = config.secondaryTimezones?.length
    ? config.secondaryTimezones.map((z) => z.timezone).join(', ')
    : 'none';

  return `You are an AI assistant that controls a schedule calendar application.

Today is: ${todayStr} (ISO: ${todayIso})

CURRENT CONFIGURATION:
${JSON.stringify(configSummary, null, 2)}

AVAILABLE RESOURCES (people / providers shown in the schedule):
${resourceList.length > 0 ? JSON.stringify(resourceList, null, 2) : 'None configured (single-user schedule).'}

WHAT YOU CAN CHANGE:

View type (configPatch.view):
  "day"       — single day
  "weekdays"  — Monday–Friday work week
  "fullWeek"  — full 7-day week
  "month"     — monthly calendar
  "agenda"    — chronological event list; use agendaDays to control the window

Time range — visible hours (configPatch.timeRange):
  Provide start and end in HH:MM 24-hour format.
  Morning:   { start:"08:00", end:"12:00" }
  Afternoon: { start:"13:00", end:"17:00" }
  Business:  { start:"08:00", end:"17:00" }
  Full day:  { start:"00:00", end:"00:00" }  ← special value for all 24 hours

Navigate to a date (navigateTo):
  Jump the calendar to a specific YYYY-MM-DD date.
  Compute dates from today: "${todayIso}".
  "Next Friday" = the upcoming Friday on or after today.

Resource filtering (resourceFilter):
  Array of resource IDs from the list above. Partial/fuzzy name match is fine.
  Empty array []  = show all configured resources.
  Omit field      = leave current filter unchanged.

Slot duration (configPatch.slotDuration):
  Valid values: 5, 10, 15, 20, 30, 45, 60, 90, 120 (minutes).

Agenda days (configPatch.agendaDays):
  Integer. Default 30. Use 35 to cover 5 weeks (ensures 5 of any given weekday).

Primary timezone (configPatch.timezone):
  IANA timezone string — changes all displayed times.
  Common values:
    US:            "America/New_York"  "America/Chicago"  "America/Denver"  "America/Los_Angeles"  "America/Phoenix"  "America/Anchorage"  "Pacific/Honolulu"
    Europe:        "Europe/London"  "Europe/Paris"  "Europe/Berlin"  "Europe/Rome"  "Europe/Madrid"  "Europe/Amsterdam"  "Europe/Zurich"
    Asia/Pacific:  "Asia/Tokyo"  "Asia/Shanghai"  "Asia/Hong_Kong"  "Asia/Singapore"  "Asia/Kolkata"  "Australia/Sydney"  "Pacific/Auckland"
    Other:         "America/Toronto"  "America/Vancouver"  "America/Sao_Paulo"  "America/Mexico_City"  "Africa/Johannesburg"  "UTC"

Secondary timezone columns (configPatch.secondaryTimezones):
  Adds extra time columns to the LEFT of the primary column.
  Example: [{ "timezone": "America/New_York", "label": "ET" }, { "timezone": "Europe/London", "label": "London" }]
  Remove all secondary columns: []
  Omit field to leave secondary columns unchanged.

Right-hand time column (configPatch.showRightTimeColumn):
  true  = mirror the time column on the RIGHT edge of the grid (useful on wide screens).
  false = hide it (default).

TV Mode (configPatch.tvMode):
  Turns the schedule into a read-only display board — hides all navigation controls
  and automatically pages through resources on a timer.
  Turn ON:  { "enabled": true, "autoRotateMs": 15000 }
            autoRotateMs controls the milliseconds per page (15000 = 15 s, 30000 = 30 s).
            Optionally add "resourcesPerPage": N to set how many resources show per page (default 4).
  Turn OFF: null

Provider Search panel (configPatch.resourcePanel):
  A searchable resource list rendered to the LEFT of the schedule grid.
  Turn ON:  { "label": "Team Members" }
  Turn OFF: null

Scheduling Queue panel (configPatch.schedulingQueue):
  A panel to the RIGHT showing unscheduled events (flagged queued:true) that users drag onto the grid.
  Turn ON:  { "label": "Queue" }
  Turn OFF: null

Event Properties panel (configPatch.propertiesPanel):
  A panel to the RIGHT that shows event details when the user clicks an event.
  Turn ON:  { "label": "Properties" }
  Turn OFF: null

CURRENT STATE:
  Primary timezone:   ${config.timezone}
  Secondary zones:    ${secondaryZones}
  Right time column:  ${config.showRightTimeColumn ? 'ON' : 'OFF'}
  TV mode:            ${panels.tvModeEnabled ? 'ON' : 'OFF'}
  Provider Search:    ${panels.showResourcePanel ? 'ON' : 'OFF'}
  Scheduling Queue:   ${panels.showSchedulingQueue ? 'ON' : 'OFF'}
  Properties panel:   ${panels.showPropertiesPanel ? 'ON' : 'OFF'}

Create event (createEvent):
  Use this whenever the user asks to schedule, create, book, or add an event or appointment.
  REQUIRED: provider must be identified — ask if not specified.
  Always also set navigateTo to the event date and resourceFilter to [resourceId].
  Set createEvent.title to the primary subject of the event (e.g. client name) when provided.
  If the schedule uses custom fields, put the collected values in createEvent.customAttributes
  as [{key, value}] pairs using the attribute keys the host app defines.
  If required fields (provider, date, time) are missing, ask for them before using createEvent.
  Once you have the required fields, use createEvent — the user fills in the rest in the form.
  Example: { resourceId: "dr-jones-id", date: "2026-06-20", startTime: "14:00", durationMinutes: 30,
             title: "Jane Smith", customAttributes: [{key:"visitType",value:"in-person"}] }

COMMON PATTERNS:
• "Next N Fridays/Mondays for [person]"
    → view:"agenda", agendaDays: N×7, navigateTo: next occurrence of that weekday,
      resourceFilter: [matched ID]
• "Afternoon only"                  → timeRange { start:"13:00", end:"17:00" }
• "Morning only"                    → timeRange { start:"08:00", end:"12:00" }
• "Business hours"                  → timeRange { start:"08:00", end:"17:00" }
• "Show [person]"                   → resourceFilter: [their ID]
• "Show everyone"                   → resourceFilter: []
• "Reset the view"                  → restore sensible defaults (weekdays view, business hours)
• "Switch to Eastern time"          → timezone: "America/New_York"
• "Add London as a secondary zone"  → secondaryTimezones: [...current..., { "timezone": "Europe/London", "label": "London" }]
• "Remove extra timezone columns"   → secondaryTimezones: []
• "Show the right time column"      → showRightTimeColumn: true
• "Hide the right time column"      → showRightTimeColumn: false
• "TV mode on"                      → tvMode: { "enabled": true, "autoRotateMs": 15000 }
• "TV mode off"                     → tvMode: null
• "Show provider search"            → resourcePanel: { "label": "Team Members" }
• "Hide provider search"            → resourcePanel: null
• "Show queue"                      → schedulingQueue: { "label": "Queue" }
• "Hide queue"                      → schedulingQueue: null
• "Show properties panel"           → propertiesPanel: { "label": "Properties" }
• "Hide properties panel"           → propertiesPanel: null
• "Schedule/create an event with [provider] on [date] at [time]"
    → createEvent: { resourceId, date, startTime }, navigateTo: date, resourceFilter: [resourceId]
• "Book a 45-minute slot with [provider] tomorrow at 3pm"
    → createEvent: { resourceId, date, startTime: "15:00", durationMinutes: 45 }

${panels.matchingEnabled ? `
Provider matching (matchingCommand) — provider matching IS currently enabled. Use matchingCommand when the user asks to filter, find, rank, or clear matched providers:
  Intents: FILTER (filter list by criteria), RANK (re-rank without hiding), CLEAR (remove all filters), QUERY (answer about who matches)
  criteria fields (all optional — include only what the user specified):
    specialty:          string[]  — e.g. ["trauma", "adolescent", "addiction"]
    modality:           string[]  — e.g. ["CBT", "DBT", "EMDR", "ACT", "ERP"]
    language:           string[]  — BCP 47: ["en", "es", "zh-TW"]
    genderPreference:   "male" | "female" | "nonbinary" | "no_preference"
    ageGroupPreference: "child" | "adolescent" | "adult" | "older_adult"
    insuranceId:        string    — e.g. "Cigna", "Aetna", "BlueCross", "UHC", "Medicaid"
    locationPreference: "in_person" | "telehealth" | "either"
  raw: always include the verbatim user message.
  Examples:
    "Find a Spanish-speaking trauma therapist"
      → { intent:"FILTER", criteria:{ language:["es"], specialty:["trauma"] }, raw:"<verbatim>" }
    "Show providers who do DBT and accept Cigna"
      → { intent:"FILTER", criteria:{ modality:["DBT"], insuranceId:"Cigna" }, raw:"<verbatim>" }
    "Clear provider filters" / "Show all providers"
      → { intent:"CLEAR", criteria:{}, raw:"<verbatim>" }
` : 'Provider matching is NOT currently enabled — do not use matchingCommand.'}

${panels.acuityEnabled ? `
Acuity slot reservation (acuityCommand) — acuity slots ARE currently enabled. Use acuityCommand when the user asks to hold, reserve, release, or query reserved/acuity slots:
  Acuity levels: urgent (critical/crisis), high (priority/post-discharge), routine (standard hold)
  Examples:
    "Hold 2 urgent slots on Tuesday for Dr. Patel"
      → acuityCommand: { intent:"RESERVE", resourceId:"<id>", date:"YYYY-MM-DD", count:2, acuityLevel:"urgent", label:"Urgent hold", raw:"<verbatim>" }
    "Reserve a crisis follow-up slot tomorrow morning"
      → acuityCommand: { intent:"RESERVE", date:"YYYY-MM-DD", count:1, acuityLevel:"urgent", label:"Crisis follow-up", raw:"<verbatim>" }
    "Release the urgent holds on Friday"
      → acuityCommand: { intent:"RELEASE", date:"YYYY-MM-DD", acuityLevel:"urgent", raw:"<verbatim>" }
    "How many priority slots are reserved this week?"
      → acuityCommand: { intent:"QUERY", raw:"<verbatim>" }
    "Mark the 2pm slot on Thursday as post-discharge hold"
      → acuityCommand: { intent:"RESERVE", date:"YYYY-MM-DD", acuityLevel:"high", label:"Post-discharge", raw:"<verbatim>" }
  Always include raw with the verbatim user message.
` : 'Acuity slot reservation is NOT currently enabled — do not use acuityCommand.'}

${panels.noShowRiskEnabled ? (() => {
  const scores = panels.riskScores;
  const byLevel = { low: 0, moderate: 0, high: 0, critical: 0 };
  for (const s of scores) byLevel[s.riskLevel] = (byLevel[s.riskLevel] ?? 0) + 1;
  const details = scores
    .filter(s => s.riskLevel === 'high' || s.riskLevel === 'critical')
    .map(s => `  • ${s.appointmentId} | ${s.riskLevel} (score ${s.riskScore}) | factors: ${s.riskFactors.join(', ')}`)
    .join('\n');
  return `No-show risk (riskCommand) — risk scoring IS currently enabled.
CURRENT RISK SUMMARY (${scores.length} scored appointments):
  low: ${byLevel.low}  moderate: ${byLevel.moderate}  high: ${byLevel.high}  critical: ${byLevel.critical}
${details ? `HIGH / CRITICAL APPOINTMENTS:\n${details}` : 'No high or critical appointments in the current view.'}

Use riskCommand when the user asks about risk levels, unconfirmed appointments, outreach, or reminders:
  Intents: QUERY (answer about risk/confirmations), FLAG_OUTREACH (flag for manual outreach),
           SUPPRESS (stop reminders for an appointment), RESEND (retrigger a reminder)
  Fields: appointmentId?, resourceId?, dateRange?: { start, end }
  For QUERY intents you already have the data above — answer directly in reply.
  Always include raw with the verbatim user message.
  Examples:
    "Show me all high-risk appointments this week"
      → riskCommand: { intent:"QUERY", dateRange:{start,end}, raw:"<verbatim>" }
        reply: list the high/critical appointments from the data above.
    "Flag all unconfirmed high-risk for outreach"
      → riskCommand: { intent:"FLAG_OUTREACH", raw:"<verbatim>" }
    "Resend the reminder for the 2pm Thursday appointment"
      → riskCommand: { intent:"RESEND", appointmentId:"<id>", raw:"<verbatim>" }`;
})() : 'No-show risk is NOT currently enabled — do not use riskCommand.'}

Reply in a friendly, concise 1-2 sentence conversational tone confirming what changed.
If a resource name is ambiguous, pick the closest match and mention it in the reply.
If the user's request is unrelated to schedule display (e.g. a general question),
still call the tool but set reply to a polite explanation and omit configPatch.`;
}

// ─── Per-resource working hours editor ────────────────────────────────────

const DAYS_OF_WEEK = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
] as const;

function resolvedDayHours(
  wh: WorkingHours | undefined,
  dayOfWeek: number,
): { enabled: boolean; start: string; end: string } {
  if (!wh) {
    return { enabled: [1, 2, 3, 4, 5].includes(dayOfWeek), start: '08:00', end: '17:00' };
  }
  const entry = wh.daySchedule?.find((d) => d.dayOfWeek === dayOfWeek);
  if (entry) return { enabled: true, start: entry.start, end: entry.end };
  const enabled = (wh.daysOfWeek ?? [1, 2, 3, 4, 5]).includes(dayOfWeek);
  return { enabled, start: wh.start, end: wh.end };
}

function applyDayChange(
  resource: Resource,
  dayOfWeek: number,
  patch: Partial<{ enabled: boolean; start: string; end: string }>,
): Resource {
  const wh = resource.workingHours;
  const schedule: DayWorkingHours[] = DAYS_OF_WEEK.flatMap(({ value }) => {
    const cur = resolvedDayHours(wh, value);
    const final = value === dayOfWeek ? { ...cur, ...patch } : cur;
    return final.enabled ? [{ dayOfWeek: value, start: final.start, end: final.end }] : [];
  });
  return {
    ...resource,
    workingHours: { start: wh?.start ?? '08:00', end: wh?.end ?? '17:00', daySchedule: schedule },
  };
}

function ResourceHoursEditor({
  resource,
  onChange,
}: {
  resource: Resource;
  onChange: (updated: Resource) => void;
}) {
  return (
    <div className="resource-hours-item">
      <div className="resource-hours-header">
        <span className="resource-dot" style={{ background: resource.color ?? '#64748b' }} />
        {resource.name}
      </div>
      <div className="resource-hours-grid">
        {DAYS_OF_WEEK.map(({ label, value: dow }) => {
          const { enabled, start, end } = resolvedDayHours(resource.workingHours, dow);
          return (
            <div key={dow} className={`resource-hours-row${enabled ? '' : ' off'}`}>
              <span className="resource-hours-day">{label}</span>
              <input
                type="checkbox"
                checked={enabled}
                style={{ accentColor: resource.color, cursor: 'pointer' }}
                onChange={(e) => onChange(applyDayChange(resource, dow, { enabled: e.target.checked }))}
              />
              <input
                type="time"
                className="ctrl-time"
                value={start}
                disabled={!enabled}
                onChange={(e) => onChange(applyDayChange(resource, dow, { start: e.target.value }))}
              />
              <input
                type="time"
                className="ctrl-time"
                value={end}
                disabled={!enabled}
                onChange={(e) => onChange(applyDayChange(resource, dow, { end: e.target.value }))}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────

// ─── Header date format presets ───────────────────────────────────────────

interface DateFormatOption {
  label: string;
  value: Intl.DateTimeFormatOptions | undefined; // undefined → library default
}

const DAY_FORMAT_OPTIONS: DateFormatOption[] = [
  { label: 'Default  (Friday, June 14, 2025)',    value: undefined },
  { label: 'Short    (Fri, Jun 14, 2025)',         value: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' } },
  { label: 'Date only (June 14, 2025)',            value: { month: 'long', day: 'numeric', year: 'numeric' } },
  { label: 'Numeric  (6/14/2025)',                 value: { month: 'numeric', day: 'numeric', year: 'numeric' } },
];

const WEEK_MONTH_FORMAT_OPTIONS: DateFormatOption[] = [
  { label: 'Default  (June 2025)',  value: undefined },
  { label: 'Short    (Jun 2025)',   value: { month: 'short', year: 'numeric' } },
  { label: 'Numeric  (6/2025)',     value: { month: 'numeric', year: 'numeric' } },
  { label: 'Month only (June)',     value: { month: 'long' } },
];

const AGENDA_FORMAT_OPTIONS: DateFormatOption[] = [
  { label: 'Default  (Jun 15 – Jul 14, 2025)',    value: undefined },
  { label: 'Month + year  (June 2025)',            value: { month: 'long', year: 'numeric' } },
  { label: 'Short  (Jun 2025)',                    value: { month: 'short', year: 'numeric' } },
  { label: 'Numeric  (6/2025)',                    value: { month: 'numeric', year: 'numeric' } },
];

interface AgendaGroupPreset { label: string; value: Intl.DateTimeFormatOptions }
const AGENDA_GROUP_FORMAT_PRESETS: AgendaGroupPreset[] = [
  { label: 'Short    (Mon, Jun 15, 2026)',         value: { weekday: 'short', month: 'short',   day: 'numeric', year: 'numeric' } },
  { label: 'Long     (Monday, June 15, 2026)',     value: { weekday: 'long',  month: 'long',    day: 'numeric', year: 'numeric' } },
  { label: 'Numeric  (Mon, 6/15/2026)',            value: { weekday: 'short', month: 'numeric', day: 'numeric', year: 'numeric' } },
  { label: 'No weekday  (Jun 15, 2026)',           value: { month: 'short',   day: 'numeric',   year: 'numeric' } },
  { label: 'No year   (Mon, Jun 15)',              value: { weekday: 'short', month: 'short',   day: 'numeric' } },
];

const VIEW_FORMAT_OPTIONS: Record<ViewType, DateFormatOption[]> = {
  day:      DAY_FORMAT_OPTIONS,
  weekdays: WEEK_MONTH_FORMAT_OPTIONS,
  fullWeek: WEEK_MONTH_FORMAT_OPTIONS,
  month:    WEEK_MONTH_FORMAT_OPTIONS,
  agenda:   AGENDA_FORMAT_OPTIONS,
};

// ─── Column header format presets ─────────────────────────────────────────

interface ColHeaderPreset {
  label: string;
  value: Intl.DateTimeFormatOptions | undefined;
}

const COL_HEADER_PRESETS_DAY_WEEK: ColHeaderPreset[] = [
  { label: 'Kendo default',          value: undefined },
  { label: 'Mon 6/14',               value: { weekday: 'short', month: 'numeric', day: 'numeric' } },
  { label: 'Monday 6/14',            value: { weekday: 'long',  month: 'numeric', day: 'numeric' } },
  { label: 'Mon Jun 14',             value: { weekday: 'short', month: 'short',   day: 'numeric' } },
  { label: 'Monday, June 14',        value: { weekday: 'long',  month: 'long',    day: 'numeric' } },
];

const COL_HEADER_PRESETS_MONTH: ColHeaderPreset[] = [
  { label: 'Kendo default',          value: undefined },
  { label: 'Mon (abbreviated)',       value: { weekday: 'short' } },
  { label: 'Monday (full)',           value: { weekday: 'long'  } },
  { label: 'M (narrow)',             value: { weekday: 'narrow' } },
];

const COL_HEADER_PRESETS: Record<ViewType, ColHeaderPreset[]> = {
  day:      COL_HEADER_PRESETS_DAY_WEEK,
  weekdays: COL_HEADER_PRESETS_DAY_WEEK,
  fullWeek: COL_HEADER_PRESETS_DAY_WEEK,
  month:    COL_HEADER_PRESETS_MONTH,
  agenda:   [],
};

const VIEW_LABELS: Record<ViewType, string> = {
  day:      'Day',
  weekdays: 'Work Week',
  fullWeek: 'Week',
  month:    'Month',
  agenda:   'Agenda',
};

const TOOLBAR_BUTTONS: { key: keyof ToolbarButtonsConfig; label: string }[] = [
  { key: 'export',   label: 'Export' },
  { key: 'day',      label: 'Day' },
  { key: 'workWeek', label: 'Work Week' },
  { key: 'week',     label: 'Week' },
  { key: 'month',    label: 'Month' },
  { key: 'agenda',   label: 'Agenda' },
  { key: 'timezone', label: 'Timezone' },
];

const US_TIMEZONES = [
  { label: 'Eastern', value: 'America/New_York' },
  { label: 'Central', value: 'America/Chicago' },
  { label: 'Mountain', value: 'America/Denver' },
  { label: 'Pacific', value: 'America/Los_Angeles' },
  { label: 'Alaska', value: 'America/Anchorage' },
  { label: 'Hawaii', value: 'Pacific/Honolulu' },
];

const WORLD_TIMEZONES = [
  { label: 'UTC',                  value: 'UTC' },
  { label: 'Eastern (New York)',   value: 'America/New_York' },
  { label: 'Central (Chicago)',    value: 'America/Chicago' },
  { label: 'Mountain (Denver)',    value: 'America/Denver' },
  { label: 'Pacific (LA)',         value: 'America/Los_Angeles' },
  { label: 'Alaska',               value: 'America/Anchorage' },
  { label: 'Hawaii',               value: 'Pacific/Honolulu' },
  { label: 'São Paulo',            value: 'America/Sao_Paulo' },
  { label: 'London',               value: 'Europe/London' },
  { label: 'Paris',                value: 'Europe/Paris' },
  { label: 'Helsinki',             value: 'Europe/Helsinki' },
  { label: 'Moscow',               value: 'Europe/Moscow' },
  { label: 'Dubai',                value: 'Asia/Dubai' },
  { label: 'Kolkata',              value: 'Asia/Kolkata' },
  { label: 'Bangkok',              value: 'Asia/Bangkok' },
  { label: 'Singapore',            value: 'Asia/Singapore' },
  { label: 'Shanghai',             value: 'Asia/Shanghai' },
  { label: 'Tokyo',                value: 'Asia/Tokyo' },
  { label: 'Sydney',               value: 'Australia/Sydney' },
  { label: 'Auckland',             value: 'Pacific/Auckland' },
];

const TIME_FORMAT_OPTIONS: { label: string; value: TimeFormat }[] = [
  { label: '12h  —  8:00 AM / 12:30 PM', value: '12h' },
  { label: 'A/P  —  8A / 8:30P',          value: '12h-short' },
  { label: '24h  —  08:00 / 12:30',       value: '24h' },
];

interface LogEntry {
  id: number;
  time: string;
  type: string;
  detail: string;
  payload?: unknown;
}

let logCounter = 0;

const SIDEBAR_MIN = 200;
const SIDEBAR_MAX = 560;
const SIDEBAR_DEFAULT = 280;

interface ConfigSnapshot {
  config: ScheduleConfig;
  showResources: boolean;
  resourcesPerPage: Partial<Record<ViewType, number>>;
  filteredResourceIds: string[];
  resources: Resource[];
  showResourcePanel: boolean;
  resourcePanelLabel: string;
  showHolidays: boolean;
  customHolidays: HolidayDefinition[];
  showEventIcons: boolean;
  formatIndexes: Record<string, number>;
  colHeaderIndexes: Record<string, number>;
  agendaGroupFormatIndex: number;
  sseEnabled: boolean;
  sseUrl: string;
  validation: ScheduleValidation;
}

interface SavedConfig {
  id: string;
  name: string;
  savedAt: string;
  snapshot: ConfigSnapshot;
}

const LS_KEY = 'fulcrum-saved-configs';

function loadSavedConfigs(): SavedConfig[] {
  try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]'); }
  catch { return []; }
}

function persistSavedConfigs(configs: SavedConfig[]): void {
  localStorage.setItem(LS_KEY, JSON.stringify(configs));
}

// ─── Built-in default configs (seeded once into localStorage) ────────────────

const LS_INIT_KEY = 'fulcrum-saved-configs-v6';

const BH_BASE_SNAPSHOT: Omit<ConfigSnapshot, 'config'> = {
  showResources: true,
  resourcesPerPage: { day: 4, weekdays: 4, fullWeek: 3, month: 4, agenda: 4 },
  filteredResourceIds: BH_PROVIDERS.map((r) => r.id),
  resources: BH_PROVIDERS,
  showResourcePanel: true,
  resourcePanelLabel: 'Therapists',
  showHolidays: true,
  customHolidays: [],
  showEventIcons: true,
  formatIndexes: { day: 0, weekdays: 0, fullWeek: 0, month: 0, agenda: 0 },
  colHeaderIndexes: { day: 1, weekdays: 1, fullWeek: 1, month: 1, agenda: 0 },
  agendaGroupFormatIndex: 0,
  sseEnabled: false,
  sseUrl: 'http://localhost:3001/events',
  validation: {},
};

const SEEDED_AT = '2026-01-01T00:00:00.000Z';

const DEFAULT_SAVED_CONFIGS: SavedConfig[] = [
  // 1. Granular day view for individual clinicians
  {
    id: 'bh-day-clinician',
    name: 'Day — Clinician Detail',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      resourcesPerPage: { day: 1, weekdays: 1, fullWeek: 1, month: 1, agenda: 1 },
      config: {
        timezone: 'America/Chicago',
        view: 'day',
        headerText: 'Day — Clinician Detail',
        timeRange: { start: '07:30', end: '18:30' },
        slotDuration: 15,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        zoom: { show: true, min: 5, max: 30 },
        resourceGrouping: 'byDate',
      },
    },
  },
  // 2. Multi-provider work week for scheduling coordinators
  {
    id: 'bh-workweek-team',
    name: 'Work Week — Full Team',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      resourcesPerPage: { day: 4, weekdays: 4, fullWeek: 3, month: 4, agenda: 4 },
      config: {
        timezone: 'America/Chicago',
        view: 'weekdays',
        headerText: 'Work Week — Full Team',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 60,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        zoom: { show: true, min: 15, max: 60 },
        resourceGrouping: 'byDate',
      },
    },
  },
  // 3. Trauma specialists only — alice, carol, frank, iris
  {
    id: 'bh-workweek-trauma',
    name: 'Work Week — Trauma Team',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      filteredResourceIds: ['alice', 'carol', 'frank', 'iris'],
      resourcesPerPage: { day: 4, weekdays: 4, fullWeek: 4, month: 4, agenda: 4 },
      config: {
        timezone: 'America/Chicago',
        view: 'weekdays',
        headerText: 'Work Week — Trauma Team',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 30,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        zoom: { show: true, min: 15, max: 60 },
        resourceGrouping: 'byDate',
      },
    },
  },
  // 4. Child and family team — alice, dave, grace, jack
  {
    id: 'bh-workweek-child-family',
    name: 'Work Week — Child & Family',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      filteredResourceIds: ['alice', 'dave', 'grace', 'jack'],
      resourcesPerPage: { day: 4, weekdays: 4, fullWeek: 4, month: 4, agenda: 4 },
      config: {
        timezone: 'America/Chicago',
        view: 'weekdays',
        headerText: 'Work Week — Child & Family',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 60,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        zoom: { show: true, min: 15, max: 60 },
        resourceGrouping: 'byDate',
      },
    },
  },
  // 5. Full 7-day view for practices with weekend/on-call coverage
  {
    id: 'bh-fullweek-oncall',
    name: 'Full Week — On-Call Coverage',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      resourcesPerPage: { day: 3, weekdays: 3, fullWeek: 3, month: 3, agenda: 3 },
      config: {
        timezone: 'America/Chicago',
        view: 'fullWeek',
        headerText: 'Full Week — On-Call Coverage',
        timeRange: { start: '07:00', end: '20:00' },
        slotDuration: 60,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [0, 1, 2, 3, 4, 5, 6] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        zoom: { show: true, min: 30, max: 120 },
        resourceGrouping: 'byDate',
      },
    },
  },
  // 6. Monthly admin/capacity planning
  {
    id: 'bh-month-capacity',
    name: 'Month — Capacity Planning',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      colHeaderIndexes: { ...BH_BASE_SNAPSHOT.colHeaderIndexes, month: 2 },
      resourcesPerPage: { day: 4, weekdays: 4, fullWeek: 3, month: 4, agenda: 4 },
      config: {
        timezone: 'America/Chicago',
        view: 'month',
        headerText: 'Month — Capacity Planning',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 60,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        resourceGrouping: 'byDate',
      },
    },
  },
  // 7. Rolling 30-day agenda for appointment overview
  {
    id: 'bh-agenda-30day',
    name: 'Agenda — Upcoming Sessions',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      agendaGroupFormatIndex: 0,
      config: {
        timezone: 'America/Chicago',
        view: 'agenda',
        headerText: 'Agenda — Upcoming Sessions',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 60,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        agendaDays: 30,
        agendaGroupFormat: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
        resourceGrouping: 'byDate',
      },
    },
  },
  // 8. Mobile-optimised agenda — minimal toolbar, vertical list layout
  {
    id: 'bh-mobile-agenda',
    name: 'Mobile — Agenda',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      showResourcePanel: false,
      resourcesPerPage: { day: 1, weekdays: 1, fullWeek: 1, month: 1, agenda: 1 },
      agendaGroupFormatIndex: 0,
      config: {
        timezone: 'America/Chicago',
        view: 'agenda',
        headerText: 'Mobile — Agenda',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 30,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        agendaDays: 1,
        agendaGroupFormat: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
        resourceGrouping: 'byDate',
        showTimezone: false,
        toolbarButtons: {
          day:      { hidden: true },
          workWeek: { hidden: true },
          week:     { hidden: true },
          month:    { hidden: true },
          timezone: { hidden: true },
          export:   { hidden: true },
        },
      },
    },
  },
  // 9. Mobile-optimised day view — 1 resource per page, time-grid layout
  {
    id: 'bh-mobile-day',
    name: 'Mobile — Day',
    savedAt: SEEDED_AT,
    snapshot: {
      ...BH_BASE_SNAPSHOT,
      showResourcePanel: false,
      resourcesPerPage: { day: 1, weekdays: 1, fullWeek: 1, month: 1, agenda: 1 },
      config: {
        timezone: 'America/Chicago',
        view: 'day',
        headerText: 'Mobile — Day',
        timeRange: { start: '08:00', end: '18:00' },
        slotDuration: 30,
        workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
        customAttributeDefinitions: BH_CUSTOM_ATTRS,
        allowEventCreation: true,
        showAllDaySection: false,
        timeFormat: '12h',
        observeDaylightSaving: true,
        resourceGrouping: 'byDate',
        showTimezone: false,
        toolbarButtons: {
          workWeek: { hidden: true },
          week:     { hidden: true },
          month:    { hidden: true },
          agenda:   { hidden: true },
          timezone: { hidden: true },
          export:   { hidden: true },
        },
      },
    },
  },
];

function seedDefaultConfigs(): void {
  if (localStorage.getItem(LS_INIT_KEY)) return;
  persistSavedConfigs(DEFAULT_SAVED_CONFIGS);
  localStorage.setItem(LS_INIT_KEY, '1');
}

seedDefaultConfigs();


export default function App() {
  const [events, setEvents] = useState<ScheduleEvent[]>(() => [...generateBhEvents(), ...generateGroupTherapyEvents()]);
  const [config, setConfig] = useState<ScheduleConfig>(() => getBhConfig());
  const [log, setLog] = useState<LogEntry[]>([]);
  const [expandedLogIds, setExpandedLogIds] = useState<Set<number>>(new Set());
  const [showResources, setShowResources] = useState(true);
  const [resourcesPerPage, setResourcesPerPage] = useState<Partial<Record<ViewType, number>>>({
    day: 4, weekdays: 4, fullWeek: 4, month: 4, agenda: 4,
  });
  const [filteredResourceIds, setFilteredResourceIds] = useState<Set<string>>(
    new Set(BH_PROVIDERS.map((r) => r.id)),
  );
  const [resources, setResources] = useState<Resource[]>(BH_PROVIDERS);
  const [showResourcePanel, setShowResourcePanel] = useState(true);
  const [resourcePanelLabel, setResourcePanelLabel] = useState('Therapists');
  const [showSchedulingQueue, setShowSchedulingQueue] = useState(false);
  const [schedulingQueueLabel, setSchedulingQueueLabel] = useState('Queue');
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [showHolidays, setShowHolidays] = useState(true);
  const [customHolidays, setCustomHolidays] = useState<HolidayDefinition[]>([]);
  const [newHolidayName, setNewHolidayName] = useState('');
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayColor, setNewHolidayColor] = useState('#6366f1');
  const [newHolidayRecurring, setNewHolidayRecurring] = useState(false);
  const [showEventIcons, setShowEventIcons] = useState(true);
  const [formatIndexes, setFormatIndexes] = useState<Record<ViewType, number>>({ day: 0, weekdays: 0, fullWeek: 0, month: 0, agenda: 0 });
  const [colHeaderIndexes, setColHeaderIndexes] = useState<Record<ViewType, number>>({ day: 0, weekdays: 0, fullWeek: 0, month: 0, agenda: 0 });
  const [agendaGroupFormatIndex, setAgendaGroupFormatIndex] = useState(0);
  const [validation, setValidation] = useState<ScheduleValidation>({});
  const [sidebarTab, setSidebarTab] = useState<'config' | 'log'>('config');
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 768);
  const [sectionSignal, setSectionSignal] = useState<{ collapsed: boolean } | null>(null);
  const [savedConfigs, setSavedConfigs] = useState<SavedConfig[]>(loadSavedConfigs);
  const [activeConfigId, setActiveConfigId] = useState<string | null>(null);
  const [showSaveAsInput, setShowSaveAsInput] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const sidebarRef = useRef<HTMLElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  // ─── TV Mode state ───────────────────────────────────────────────────────
  const [tvModeEnabled, setTvModeEnabled] = useState(false);
  const [tvLogoUrl, setTvLogoUrl] = useState('');
  const [tvResourcesPerPage, setTvResourcesPerPage] = useState(4);
  const [tvAutoRotateSec, setTvAutoRotateSec] = useState(15);
  const [tvResourceIds, setTvResourceIds] = useState<Set<string>>(new Set());

  // ─── AI Chat state ───────────────────────────────────────────────────────
  const [showAiChatPanel, setShowAiChatPanel] = useState(false);
  const [aiApiKey, setAiApiKey] = useState<string>(
    () => localStorage.getItem('fulcrum-ai-api-key') ?? '',
  );

  // ─── Intake Screener Gate state ──────────────────────────────────────────
  const [screenerEnabled, setScreenerEnabled] = useState(false);
  const [screenerScenario, setScreenerScenario] = useState<'PHQ9' | 'GAD7' | 'CSSRS' | 'AUDIT_C' | 'DAST10' | 'skip'>('PHQ9');
  const [screenerOutcome, setScreenerOutcome] = useState<'confirm' | 'adjust' | 'crisis'>('confirm');

  // ─── Acuity Slot Reservation state ───────────────────────────────────────
  const [acuityEnabled, setAcuityEnabled] = useState(false);
  const [acuityPrivilegedView, setAcuityPrivilegedView] = useState(false);
  // Stable release time — computed once so view navigation doesn't reset the countdown.
  const demoReleaseAtRef = useRef<string | null>(null);

  // ─── Provider Matching state ─────────────────────────────────────────────
  const [matchingEnabled, setMatchingEnabled] = useState(false);
  const [matchingScenario, setMatchingScenario] = useState<'trauma-spanish' | 'cbt-cigna' | 'telehealth-adolescent' | 'mandarin'>('trauma-spanish');

  // ─── Waitlist / Auto-Backfill state ──────────────────────────────────────
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [waitlistScenario, setWaitlistScenario] = useState<'match' | 'no-match'>('match');

  // ─── No-Show Risk state ───────────────────────────────────────────────────
  const [noShowRiskEnabled, setNoShowRiskEnabled] = useState(false);
  const [cachedRiskScores, setCachedRiskScores] = useState<AppointmentRiskScore[]>([]);

  // ─── Group Therapy state ─────────────────────────────────────────────────
  const [groupTherapyEnabled, setGroupTherapyEnabled] = useState(false);
  const [demoEnrollments, setDemoEnrollments] = useState<Record<string, GroupEnrollment[]>>({});

  // ─── Custom double-click form state ──────────────────────────────────────
  const [useCustomDoubleClickForm, setUseCustomDoubleClickForm] = useState(false);
  const [customFormEvent, setCustomFormEvent] = useState<ScheduleEvent | null>(null);
  const [newEventSlot, setNewEventSlot] = useState<{ startUtc: string; endUtc: string; resourceId?: string } | null>(null);
  // Tracks the scheduler's live timezone — updated via onTimezoneChange when the
  // user changes it in the embedded toolbar (which doesn't sync back to config).
  const [liveTimezone, setLiveTimezone] = useState(config.timezone);
  // Also keep in sync when the host panel changes config.timezone directly.
  useEffect(() => { setLiveTimezone(config.timezone); }, [config.timezone]);

  // ─── SSE live-update state ───────────────────────────────────────────────
  const [sseEnabled, setSseEnabled] = useState(false);
  const [sseUrl, setSseUrl] = useState('http://localhost:3001/events');

  // When TV mode is enabled, ensure resources are shown, the resource panel is
  // visible, and grouping is byDate. If no resources were selected, select all.
  React.useEffect(() => {
    if (!tvModeEnabled) return;
    setShowResources(true);
    setShowResourcePanel(true);
    setFilteredResourceIds((prev) => {
      if (prev.size === 0) return new Set(resources.map((r) => r.id));
      return prev;
    });
    setConfig((prev) => ({ ...prev, resourceGrouping: 'byDate' }));
  }, [tvModeEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    if (!sidebarRef.current) return;
    const startX = e.clientX;
    const startWidth = sidebarRef.current.offsetWidth;

    const onMouseMove = (ev: MouseEvent) => {
      const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startWidth + ev.clientX - startX));
      if (sidebarRef.current) sidebarRef.current.style.width = `${w}px`;
    };
    const onMouseUp = (ev: MouseEvent) => {
      const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startWidth + ev.clientX - startX));
      setSidebarWidth(w);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    e.preventDefault();
  }, []);

  const addLog = useCallback((type: string, detail: string, payload?: unknown) => {
    setLog((prev) => {
      const entry: LogEntry = {
        id: ++logCounter,
        time: new Date().toLocaleTimeString(),
        type,
        detail,
        payload,
      };
      const next = [entry, ...prev].slice(0, 200);
      return next;
    });
  }, []);

  // ─── SSE event handlers ──────────────────────────────────────────────────
  const handleSseAdd = useCallback((newEvents: ScheduleEvent[]) => {
    addLog('SSE_ADD', `${newEvents.length} event(s) received`);
    setEvents((prev) => [
      ...prev.filter((e) => !newEvents.some((n) => n.id === e.id)),
      ...newEvents,
    ]);
  }, [addLog]);

  const handleSseUpdate = useCallback((updated: ScheduleEvent) => {
    addLog('SSE_UPDATE', `"${updated.title}" (${updated.id})`);
    setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  }, [addLog]);

  const handleSseRemove = useCallback((eventId: string) => {
    addLog('SSE_REMOVE', eventId);
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
  }, [addLog]);

  const sseStatus: ConnectionStatus = useSseSchedule({
    url: sseUrl,
    enabled: sseEnabled,
    onAddEvents: handleSseAdd,
    onUpdateEvent: handleSseUpdate,
    onRemoveEvent: handleSseRemove,
  });

  // ─── AI message handler ──────────────────────────────────────────────────
  const handleAiMessage = useCallback(async (
    message: string,
    context: AiMessageContext,
  ): Promise<AiResponse> => {
    const key = aiApiKey.trim() || (import.meta.env.VITE_ANTHROPIC_API_KEY as string | undefined) || '';
    if (!key) {
      throw new Error(
        'No Claude API key found.\n\nAdd your key in the sidebar under "AI Assistant", or set VITE_ANTHROPIC_API_KEY in packages/host/.env.local',
      );
    }

    const panels: AiPanelState = {
      tvModeEnabled,
      showResourcePanel,
      showSchedulingQueue,
      showPropertiesPanel,
      acuityEnabled,
      matchingEnabled,
      noShowRiskEnabled,
      riskScores: cachedRiskScores,
    };

    // Pass all resources (not just visible) so the LLM can match names regardless of current filter
    const systemPrompt = buildAiSystemPrompt(config, resources, panels);

    const client = new Anthropic({ apiKey: key, dangerouslyAllowBrowser: true });
    const history = context.conversationHistory ?? [];
    const data = await client.messages.create({
      model: CLAUDE_MODEL,
      max_tokens: 1024,
      system: systemPrompt,
      messages: [...history, { role: 'user', content: message }],
      tools: [SCHEDULE_AI_TOOL as Anthropic.Tool],
      tool_choice: { type: 'tool', name: 'apply_schedule_change' },
    });

    const toolBlock = data.content.find((b) => b.type === 'tool_use');
    if (!toolBlock || toolBlock.type !== 'tool_use') throw new Error('No response received from AI.');

    type AiPatch = Partial<ScheduleConfig> & {
      tvMode?: TvModeConfig | null;
      resourcePanel?: ResourcePanelConfig | null;
      schedulingQueue?: SchedulingQueueConfig | null;
      propertiesPanel?: PropertiesPanelConfig | null;
    };
    const result = (toolBlock as Anthropic.ToolUseBlock).input as {
      reply: string;
      configPatch?: AiPatch;
      navigateTo?: string;
      resourceFilter?: string[];
      createEvent?: AiCreateEventRequest;
      acuityCommand?: AcuityCommandPayload;
      matchingCommand?: MatchingCommandPayload;
      riskCommand?: RiskCommandPayload;
    };

    if (result.configPatch) {
      const {
        tvMode,
        resourcePanel,
        schedulingQueue,
        propertiesPanel,
        ...configOnly
      } = result.configPatch;

      // Panel toggles — each is driven by its own state variable in the host app
      if ('tvMode' in result.configPatch) {
        setTvModeEnabled(!!tvMode?.enabled);
        if (tvMode?.autoRotateMs !== undefined) setTvAutoRotateSec(Math.round(tvMode.autoRotateMs / 1000));
        if (tvMode?.resourcesPerPage !== undefined) setTvResourcesPerPage(tvMode.resourcesPerPage);
      }
      if ('resourcePanel' in result.configPatch) setShowResourcePanel(resourcePanel != null);
      if ('schedulingQueue' in result.configPatch) setShowSchedulingQueue(schedulingQueue != null);
      if ('propertiesPanel' in result.configPatch) setShowPropertiesPanel(propertiesPanel != null);

      // Config-level fields (timezone, secondaryTimezones, showRightTimeColumn, view, timeRange, etc.)
      if (Object.keys(configOnly).length > 0) {
        setConfig((prev) => ({ ...prev, ...configOnly }));
      }
    }

    if (result.resourceFilter !== undefined) {
      const ids = result.resourceFilter.length === 0
        ? new Set(resources.map((r) => r.id))
        : new Set(result.resourceFilter);
      if (!showResources) setShowResources(true);
      setFilteredResourceIds(ids);
    }

    return { reply: result.reply, navigateTo: result.navigateTo, createEvent: result.createEvent, acuityCommand: result.acuityCommand, matchingCommand: result.matchingCommand, riskCommand: result.riskCommand };
  }, [
    aiApiKey, config, resources, acuityEnabled, matchingEnabled, noShowRiskEnabled, cachedRiskScores,
    tvModeEnabled, showResourcePanel, showSchedulingQueue, showPropertiesPanel,
    showResources, setConfig, setFilteredResourceIds, setShowResources,
    setTvModeEnabled, setTvAutoRotateSec, setTvResourcesPerPage,
    setShowResourcePanel, setShowSchedulingQueue, setShowPropertiesPanel,
  ]);

  const callbacks: ScheduleCallbacks = {
    onRequestData: (range) => {
      const resourceNote = range.resourceIds?.length ? ` [${range.resourceIds.length} resources]` : '';
      addLog('REQUEST_DATA', `${range.startUtc.split('T')[0]} → ${range.endUtc.split('T')[0]}${resourceNote}`, range);
    },
    onEventMoved: (payload: EventMovedPayload) => {
      addLog('EVENT_MOVED', `"${payload.eventId}" → ${payload.newStartUtc}`, payload);
      setEvents((prev) =>
        prev.map((e) =>
          e.id === payload.eventId || e.originalId === payload.eventId
            ? { ...e, startUtc: payload.newStartUtc, endUtc: payload.newEndUtc, resourceId: payload.newResourceId ?? e.resourceId }
            : e,
        ),
      );
    },
    onEventResized: (payload: EventResizedPayload) => {
      addLog('EVENT_RESIZED', `"${payload.eventId}" start:${payload.newStartUtc.split('T')[1]?.slice(0,5)} end:${payload.newEndUtc.split('T')[1]?.slice(0,5)}`, payload);
      setEvents((prev) =>
        prev.map((e) =>
          e.id === payload.eventId
            ? { ...e, startUtc: payload.newStartUtc, endUtc: payload.newEndUtc }
            : e,
        ),
      );
    },
    onEventSaved: (event: ScheduleEvent) => {
      addLog('EVENT_SAVED', `"${event.title}" (${event.id})`, event);
      setEvents((prev) => {
        const exists = prev.find((e) => e.id === event.id);
        return exists
          ? prev.map((e) => (e.id === event.id ? event : e))
          : [...prev, event];
      });
    },
    onEventDeleted: (eventId: string) => {
      addLog('EVENT_DELETED', eventId, { eventId });
      setEvents((prev) => prev.filter((e) => e.id !== eventId && e.originalId !== eventId));
    },
    onEventCreated: (event: ScheduleEvent) => {
      addLog('EVENT_CREATED', `"${event.title}" (${event.id})`, event);
      setEvents((prev) => [...prev, event]);
    },
    onSlotClick: (payload) => {
      addLog('SLOT_CLICK', payload.dateUtc, payload);
    },
    onViewChanged: (view, range) => {
      addLog('VIEW_CHANGED', `${view} | ${range.startUtc.split('T')[0]}`, { view, range });
    },
    onDateNavigated: (range) => {
      addLog('DATE_NAVIGATED', `${range.startUtc.split('T')[0]} → ${range.endUtc.split('T')[0]}`, range);
    },
    onQueuedEventSelected: (event) => {
      addLog('WAITLIST_SELECTED', `"${event.title}" (${event.id})`, event);
    },
    onQueuedEventScheduled: (event) => {
      addLog('WAITLIST_SCHEDULED', `"${event.title}" → ${event.startUtc}`, event);
      setEvents((prev) => prev.map((e) => (e.id === event.id ? event : e)));
    },
    onValidationError: (error: ValidationError) => {
      addLog('VALIDATION_ERROR', error.message, error);
    },
    onEventDoubleClick: (event: ScheduleEvent) => {
      addLog('EVENT_DOUBLE_CLICK', `"${event.title}" (${event.id})`, event);
      if (useCustomDoubleClickForm) {
        setCustomFormEvent(event);
        return true;
      }
    },
    onTimezoneChange: (tz: string) => {
      setLiveTimezone(tz);
      setConfig((prev) => ({ ...prev, timezone: tz }));
    },
    onNewEventIntercepted: (startUtc: string, endUtc: string, resourceId?: string) => {
      addLog('NEW_EVENT_INTERCEPTED', `${startUtc.split('T')[1]?.slice(0, 5)} → ${endUtc.split('T')[1]?.slice(0, 5)}${resourceId ? ` (${resourceId})` : ''}`, { startUtc, endUtc, resourceId });
      setNewEventSlot({ startUtc, endUtc, resourceId });
    },
    onTvPageChanged: ({ page, totalPages, resourceIds }) => {
      addLog('TV_PAGE_CHANGED', `Page ${page + 1} of ${totalPages} | ${resourceIds.length} resources`, { page, totalPages, resourceIds });
    },
    onAiMessage: showAiChatPanel ? handleAiMessage : undefined,
    onEventClick: (event: ScheduleEvent): PropertyItem[] => {
      addLog('EVENT_CLICK', `"${event.title}" (${event.id})`, event);
      const items: PropertyItem[] = [];
      items.push({ label: 'Client', value: event.title });
      if (event.description) items.push({ label: 'Description', value: event.description });
      if (event.startUtc) {
        items.push({ label: 'Start', value: new Date(event.startUtc).toLocaleString() });
      }
      if (event.endUtc) {
        items.push({ label: 'End', value: new Date(event.endUtc).toLocaleString() });
      }
      if (event.resourceId) {
        const res = resources.find((r) => r.id === event.resourceId);
        if (res) items.push({ label: 'Therapist', value: res.name });
      }
      event.customAttributes?.forEach((attr) => {
        if (attr.key === 'client') return;
        const def = config.customAttributeDefinitions?.find((d) => d.key === attr.key);
        if (def && attr.value != null) {
          const display = Array.isArray(attr.value) ? attr.value.join(', ') : String(attr.value);
          items.push({ label: def.label, value: display });
        }
      });
      return items;
    },
  };

  const updateConfig = useCallback(<K extends keyof ScheduleConfig>(key: K, value: ScheduleConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateDateFormat = useCallback((view: ViewType, idx: number) => {
    setFormatIndexes((prev) => ({ ...prev, [view]: idx }));
    const option = VIEW_FORMAT_OPTIONS[view][idx];
    setConfig((prev) => ({
      ...prev,
      headerDateFormats: { ...prev.headerDateFormats, [view]: option.value } as HeaderDateFormats,
    }));
  }, []);

  const updateColHeaderFormat = useCallback((view: ViewType, idx: number) => {
    setColHeaderIndexes((prev) => ({ ...prev, [view]: idx }));
    const value = COL_HEADER_PRESETS[view][idx]?.value;
    setConfig((prev) => ({
      ...prev,
      columnHeaderFormats: { ...prev.columnHeaderFormats, [view]: value } as ColumnHeaderFormats,
    }));
  }, []);

  const updateAgendaGroupFormat = useCallback((idx: number) => {
    setAgendaGroupFormatIndex(idx);
    updateConfig('agendaGroupFormat', AGENDA_GROUP_FORMAT_PRESETS[idx].value);
  }, [updateConfig]);

  const captureSnapshot = useCallback((): ConfigSnapshot => ({
    config,
    showResources,
    resourcesPerPage,
    filteredResourceIds: [...filteredResourceIds],
    resources,
    showResourcePanel,
    resourcePanelLabel,
    showHolidays,
    customHolidays,
    showEventIcons,
    formatIndexes,
    colHeaderIndexes,
    agendaGroupFormatIndex,
    sseEnabled,
    sseUrl,
    validation,
  }), [config, showResources, resourcesPerPage, filteredResourceIds, resources, showResourcePanel, resourcePanelLabel, showHolidays, customHolidays, showEventIcons, formatIndexes, colHeaderIndexes, agendaGroupFormatIndex, sseEnabled, sseUrl, validation]);

  const applySnapshot = useCallback((snap: ConfigSnapshot) => {
    setConfig(snap.config);
    setShowResources(snap.showResources);
    setResourcesPerPage(snap.resourcesPerPage);
    setFilteredResourceIds(new Set(snap.filteredResourceIds));
    setResources(snap.resources);
    setShowResourcePanel(snap.showResourcePanel);
    setResourcePanelLabel(snap.resourcePanelLabel);
    setShowHolidays(snap.showHolidays);
    setCustomHolidays(snap.customHolidays);
    setShowEventIcons(snap.showEventIcons);
    setFormatIndexes(snap.formatIndexes as Record<ViewType, number>);
    setColHeaderIndexes(snap.colHeaderIndexes as Record<ViewType, number>);
    setAgendaGroupFormatIndex(snap.agendaGroupFormatIndex);
    setSseEnabled(snap.sseEnabled);
    setSseUrl(snap.sseUrl);
    setValidation(snap.validation ?? {});
  }, []);

  const handleSaveAs = useCallback(() => {
    const name = saveAsName.trim();
    if (!name) return;
    const newEntry: SavedConfig = {
      id: crypto.randomUUID(),
      name,
      savedAt: new Date().toISOString(),
      snapshot: captureSnapshot(),
    };
    setSavedConfigs((prev) => {
      const next = [...prev, newEntry];
      persistSavedConfigs(next);
      return next;
    });
    setActiveConfigId(newEntry.id);
    setSaveAsName('');
    setShowSaveAsInput(false);
  }, [saveAsName, captureSnapshot]);

  const handleSave = useCallback(() => {
    if (!activeConfigId) return;
    setSavedConfigs((prev) => {
      const next = prev.map((c) =>
        c.id === activeConfigId
          ? { ...c, savedAt: new Date().toISOString(), snapshot: captureSnapshot() }
          : c,
      );
      persistSavedConfigs(next);
      return next;
    });
  }, [activeConfigId, captureSnapshot]);

  const handleDelete = useCallback(() => {
    if (!activeConfigId) return;
    setSavedConfigs((prev) => {
      const next = prev.filter((c) => c.id !== activeConfigId);
      persistSavedConfigs(next);
      return next;
    });
    setActiveConfigId(null);
  }, [activeConfigId]);

  const handleLoadConfig = useCallback((id: string) => {
    const found = savedConfigs.find((c) => c.id === id);
    if (!found) { setActiveConfigId(null); return; }
    applySnapshot(found.snapshot);
    setActiveConfigId(id);
    setShowSaveAsInput(false);
  }, [savedConfigs, applySnapshot]);

  const didAutoApplyMobile = useRef(false);
  useEffect(() => {
    if (didAutoApplyMobile.current || window.innerWidth >= 768) return;
    didAutoApplyMobile.current = true;
    handleLoadConfig('bh-mobile-agenda');
  }, [handleLoadConfig]);

  const toggleResources = useCallback((checked: boolean) => {
    setShowResources(checked);
    setFilteredResourceIds(checked ? new Set(resources.map((r) => r.id)) : new Set());
  }, [resources]);

  const toggleResource = useCallback((id: string, checked: boolean) => {
    setFilteredResourceIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const updateResource = useCallback((updated: Resource) => {
    setResources((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  const [isLoadingIndustry, setIsLoadingIndustry] = useState(false);

  const resetBhData = useCallback(() => {
    setIsLoadingIndustry(true);
    setTimeout(() => {
      setResources(BH_PROVIDERS);
      setFilteredResourceIds(new Set(BH_PROVIDERS.map((r) => r.id)));
      setShowResources(true);
      setShowResourcePanel(true);
      setResourcePanelLabel('Therapists');
      setResourcesPerPage({ day: 4, weekdays: 4, fullWeek: 4, month: 4, agenda: 4 });
      setEvents([...generateBhEvents(), ...generateGroupTherapyEvents()]);
      setConfig(getBhConfig());
      setShowSchedulingQueue(false);
      setIsLoadingIndustry(false);
    }, 0);
  }, []);

  const resourcePanelConfig = useMemo<ResourcePanelConfig | undefined>(() => {
    if (!showResourcePanel) return undefined;
    return {
      label: resourcePanelLabel || 'Therapists',
      columns: [
        { key: 'specialty',  label: 'Specialty', width: 160 },
        { key: 'credential', label: 'Cred',      width:  46 },
      ],
      defaultWidth: 300,
    };
  }, [showResourcePanel, resourcePanelLabel]);

  const schedulingQueueConfig = useMemo<SchedulingQueueConfig | undefined>(() => {
    if (!showSchedulingQueue) return undefined;
    return {
      label: schedulingQueueLabel || 'Queue',
      defaultWidth: 280,
      filterByVisibleResources: false,
    };
  }, [showSchedulingQueue, schedulingQueueLabel]);

  const activeHolidays = useMemo<HolidayDefinition[]>(() => {
    const base = showHolidays ? US_HOLIDAYS_2026 : [];
    return [...base, ...customHolidays];
  }, [showHolidays, customHolidays]);

  const propertiesPanelConfig = useMemo<PropertiesPanelConfig | undefined>(() => {
    if (!showPropertiesPanel) return undefined;
    return { label: 'Properties', defaultWidth: 280 };
  }, [showPropertiesPanel]);

  const aiChatPanelConfig = useMemo<AiChatPanelConfig | undefined>(() => {
    if (!showAiChatPanel) return undefined;
    const suggestedPrompts = noShowRiskEnabled
      ? RISK_PROMPT_EXAMPLES
      : matchingEnabled
      ? MATCHING_PROMPT_EXAMPLES
      : [
          'Show afternoon only (1pm–5pm)',
          'Switch to agenda view',
          'Show morning only (8am–noon)',
          'Reset to business hours',
        ];
    return { label: 'AI Assistant', defaultWidth: 360, suggestedPrompts };
  }, [showAiChatPanel, matchingEnabled, noShowRiskEnabled]);

  const screenerGate = useMemo<ScreenerGateConfig | undefined>(() => {
    if (!screenerEnabled) return undefined;
    return {
      enabled: true,
      patientId: 'demo-patient-001',
      onSlotSelected: (payload: SlotSelectedPayload): Promise<{ screener: ActiveScreener } | 'skip'> => {
        addLog('SLOT_SELECTED', `Slot selected: ${payload.start}`, payload);
        return new Promise((resolve) => {
          // Simulate host "scoring" delay
          setTimeout(() => {
            if (screenerScenario === 'skip') {
              resolve('skip');
            } else if (screenerScenario === 'GAD7') {
              resolve({ screener: GAD7_SCREENER });
            } else if (screenerScenario === 'CSSRS') {
              resolve({ screener: CSSRS_SCREENER });
            } else if (screenerScenario === 'AUDIT_C') {
              resolve({ screener: AUDIT_C_SCREENER });
            } else if (screenerScenario === 'DAST10') {
              resolve({ screener: DAST10_SCREENER });
            } else {
              resolve({ screener: PHQ9_SCREENER });
            }
          }, 200);
        });
      },
      onScreenerComplete: (payload: ScreenerCompletePayload) => {
        addLog('SCREENER_COMPLETE', `Score: ${payload.score ?? 'n/a'} — Severity: ${payload.severity}`, payload);
        return new Promise((resolve) => {
          setTimeout(() => {
            if (screenerOutcome === 'crisis') {
              addLog('SCREENER_COMPLETE', 'Crisis route — appointment NOT booked', { severity: payload.severity });
              resolve({
                action: 'CRISIS_ROUTE',
                message: 'This patient\'s responses indicate a potential crisis situation. Please review the resources below and contact the patient before scheduling.',
                resources: [
                  { label: '988 Suicide & Crisis Lifeline', phone: '988' },
                  { label: 'Crisis Text Line', phone: 'Text HOME to 741741' },
                  { label: 'SAMHSA National Helpline', phone: '1-800-662-4357', url: 'https://www.samhsa.gov/find-help/national-helpline' },
                ],
              });
            } else if (screenerOutcome === 'adjust') {
              addLog('APPOINTMENT_BOOKED', `Booked (90-min adjusted slot) — severity: ${payload.severity}`, { slotId: payload.slotId, severity: payload.severity, adjustedDuration: 90 });
              resolve({ action: 'ADJUST_SLOT', adjustedDuration: 90 });
            } else {
              const appointmentId = `apt-${Date.now()}`;
              addLog('APPOINTMENT_BOOKED', `Booked — ${appointmentId} — severity: ${payload.severity}`, { appointmentId, slotId: payload.slotId, severity: payload.severity });
              resolve({ action: 'CONFIRM', appointmentId });
            }
          }, 300);
        });
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenerEnabled, screenerScenario, screenerOutcome]);

  // ─── Acuity Slot Reservation config ──────────────────────────────────────

  const acuityConfig = useMemo<AcuitySlotConfig | undefined>(() => {
    if (!acuityEnabled) return undefined;
    return {
      enabled: true,
      releaseWarningMinutes: 30,

      onRequestAcuitySlots: (payload: AcuitySlotsRequestPayload): Promise<AcuityReservation[]> => {
        addLog('ACUITY_SLOTS_REQUEST',
          `${payload.visibleRangeStart.split('T')[0]} → ${payload.visibleRangeEnd.split('T')[0]} [${payload.resourceIds.length} resources]`,
          payload,
        );
        if (!demoReleaseAtRef.current) {
          demoReleaseAtRef.current = new Date(Date.now() + 25 * 60_000).toISOString();
        }
        const reservations = generateDemoReservations(
          payload.visibleRangeStart,
          payload.visibleRangeEnd,
          payload.resourceIds,
          demoReleaseAtRef.current,
        );
        addLog('ACUITY_SLOTS_CONFIG', `${reservations.length} demo reservations generated`, reservations);
        return Promise.resolve(reservations);
      },

      onReleaseWarning: (payload: ReleaseWarningPayload) => {
        addLog('ACUITY_RELEASE_WARNING',
          `Reservation ${payload.reservationId} — ${payload.minutesRemaining}m remaining`,
          payload,
        );
      },

      onAcuityCommand: (payload: AcuityCommandPayload): Promise<AcuityReservation[]> => {
        addLog('ACUITY_COMMAND', `${payload.intent} — "${payload.raw}"`, payload);
        // Demo: RESERVE adds a new reservation, RELEASE removes matching ones, QUERY returns current.
        // Since we don't maintain persistent state server-side, we return updated demo data.
        const today = new Date().toISOString().split('T')[0];
        const targetDate = payload.date ?? today;
        const r0 = payload.resourceId ?? resources[0]?.id ?? 'default';

        if (payload.intent === 'RELEASE') {
          addLog('ACUITY_SLOTS_CONFIG', `Releasing ${payload.acuityLevel ?? 'all'} reservations on ${targetDate}`, {});
          return Promise.resolve([]);
        }

        if (payload.intent === 'RESERVE') {
          const level = payload.acuityLevel ?? 'routine';
          const count = payload.count ?? 1;
          const newReservations: AcuityReservation[] = Array.from({ length: count }, (_, i) => ({
            id: `cmd-${Date.now()}-${i}`,
            resourceId: r0,
            start: `${targetDate}T${String(9 + i).padStart(2, '0')}:00:00.000Z`,
            end: `${targetDate}T${String(9 + i).padStart(2, '0')}:30:00.000Z`,
            acuityLevel: level,
            label: payload.label ?? (level === 'urgent' ? 'Urgent hold' : level === 'high' ? 'Priority hold' : 'Reserved'),
            visibleToGeneral: level !== 'urgent',
            releaseAt: payload.releaseAt,
          }));
          addLog('ACUITY_SLOTS_CONFIG', `${count} ${level} reservation(s) created`, newReservations);
          return Promise.resolve(newReservations);
        }

        // QUERY — return current demo data for the visible range
        addLog('ACUITY_SLOTS_CONFIG', `Query result for ${targetDate}`, {});
        return Promise.resolve(generateDemoReservations(
          `${targetDate}T00:00:00.000Z`,
          `${targetDate}T23:59:59.000Z`,
          [r0],
          demoReleaseAtRef.current ?? new Date(Date.now() + 25 * 60_000).toISOString(),
        ));
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acuityEnabled, resources]);

  // ─── Provider Matching config ─────────────────────────────────────────────

  const DEMO_PATIENT_CONTEXTS: Record<string, PatientContext> = {
    'trauma-spanish': {
      patientId: 'demo-patient-matching',
      preferences: { specialty: ['trauma'], language: ['es'], locationPreference: 'either' },
    },
    'cbt-cigna': {
      patientId: 'demo-patient-matching',
      preferences: { modality: ['CBT', 'DBT'], insuranceId: 'Cigna' },
    },
    'telehealth-adolescent': {
      patientId: 'demo-patient-matching',
      preferences: { ageGroupPreference: 'adolescent', locationPreference: 'telehealth' },
    },
    'mandarin': {
      patientId: 'demo-patient-matching',
      preferences: { language: ['zh-TW'] },
    },
  };

  const providerMatchingConfig = useMemo<ProviderMatchingConfig | undefined>(() => {
    if (!matchingEnabled) return undefined;
    return {
      enabled: true,
      onRequestMatching: (payload: MatchingRequestPayload): Promise<MatchingResultsPayload> => {
        addLog('MATCHING_REQUEST',
          `Patient: ${payload.patientContext.patientId} | ${payload.resourceIds.length} resources`,
          payload,
        );
        return new Promise((resolve) => {
          setTimeout(() => {
            const results = runDemoMatching(payload.patientContext, payload.resourceIds);
            addLog('MATCHING_RESULTS', results.matchSummary ?? 'Matching complete', results);
            resolve(results);
          }, 150);
        });
      },
      onMatchingCommand: (payload: MatchingCommandPayload): Promise<MatchingResultsPayload> => {
        addLog('MATCHING_COMMAND', `${payload.intent} — "${payload.raw}"`, payload);
        if (payload.intent === 'CLEAR') {
          return Promise.resolve({ patientId: 'demo-patient-matching', matches: [], unmatchedResourceIds: [] });
        }
        // Build a synthetic PatientContext from the command criteria.
        const syntheticCtx: PatientContext = {
          patientId: 'demo-patient-matching',
          preferences: payload.criteria,
        };
        return new Promise((resolve) => {
          setTimeout(() => {
            // We don't have real resource IDs here — use all profiled IDs.
            const allIds = DEMO_PROVIDER_PROFILES.map((p) => p.resourceId);
            const results = runDemoMatching(syntheticCtx, allIds);
            addLog('MATCHING_RESULTS', results.matchSummary ?? 'Matching complete', results);
            resolve(results);
          }, 150);
        });
      },
      onProviderSelected: (payload: ProviderSelectedPayload) => {
        addLog('PROVIDER_SELECTED',
          `Provider: ${payload.resourceId} | Score: ${payload.matchScore} | ${payload.selectedFrom}`,
          payload,
        );
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingEnabled, resources]);

  const activePatientContext = useMemo<PatientContext | undefined>(() => {
    if (!matchingEnabled) return undefined;
    return DEMO_PATIENT_CONTEXTS[matchingScenario];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingEnabled, matchingScenario]);

  // ─── Waitlist / Auto-Backfill config ─────────────────────────────────────

  const DEMO_WAITLIST_ENTRIES: WaitlistEntry[] = useMemo(() => {
    const now = Date.now();
    return [
      {
        id: 'wl-001', patientId: 'pt-001', displayLabel: 'Patient #4001',
        priority: 1, acuityLevel: 'urgent',
        waitingSince: new Date(now - 21 * 86_400_000).toISOString(),
        preferences: { timeOfDay: 'morning', locationPreference: 'in_person' },
        notes: 'Crisis follow-up — referred by ED. Needs earliest available slot.',
      },
      {
        id: 'wl-002', patientId: 'pt-002', displayLabel: 'Patient #4002',
        priority: 2, acuityLevel: 'urgent',
        waitingSince: new Date(now - 14 * 86_400_000).toISOString(),
        preferences: { timeOfDay: 'any', locationPreference: 'telehealth' },
      },
      {
        id: 'wl-003', patientId: 'pt-003', displayLabel: 'Patient #4003',
        priority: 3, acuityLevel: 'high',
        waitingSince: new Date(now - 35 * 86_400_000).toISOString(),
        preferences: { timeOfDay: 'afternoon', locationPreference: 'either' },
        notes: 'Trauma history — prefers consistent provider.',
      },
      {
        id: 'wl-004', patientId: 'pt-004', displayLabel: 'Patient #4004',
        priority: 4, acuityLevel: 'high',
        waitingSince: new Date(now - 28 * 86_400_000).toISOString(),
        preferences: { timeOfDay: 'any', locationPreference: 'in_person' },
      },
      {
        id: 'wl-005', patientId: 'pt-005', displayLabel: 'Patient #4005',
        priority: 5, acuityLevel: 'routine',
        waitingSince: new Date(now - 42 * 86_400_000).toISOString(),
        preferences: { timeOfDay: 'afternoon', locationPreference: 'telehealth' },
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waitlistConfig = useMemo<WaitlistConfig | undefined>(() => {
    if (!waitlistEnabled) return undefined;

    return {
      enabled: true,

      onRequestWaitlistState: (): Promise<WaitlistStatePayload> => {
        addLog('WAITLIST_STATE_REQUEST', `${DEMO_WAITLIST_ENTRIES.length} patients on waitlist`, {});
        return new Promise((resolve) => {
          setTimeout(() => {
            const payload: WaitlistStatePayload = {
              entries: DEMO_WAITLIST_ENTRIES,
              pendingBackfills: [],
              summary: { totalWaiting: 5, urgentCount: 2, oldestWaitDays: 42, estimatedWaitDays: 18 },
            };
            addLog('WAITLIST_STATE', `${payload.entries.length} entries returned`, payload);
            resolve(payload);
          }, 600);
        });
      },

      onSlotOpened: (payload: SlotOpenedPayload): Promise<BackfillMatchPayload | BackfillNoMatchPayload> => {
        const timeStr = new Date(payload.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        addLog('SLOT_OPENED', `Via ${payload.openedBy} — ${timeStr}`, payload);
        return new Promise((resolve) => {
          setTimeout(() => {
            if (waitlistScenario === 'no-match') {
              const noMatch: BackfillNoMatchPayload = {
                slotId: payload.slotId,
                reason: 'no_preference_match',
              };
              addLog('BACKFILL_NO_MATCH', 'No suitable patient found', noMatch);
              resolve(noMatch);
            } else {
              const top = DEMO_WAITLIST_ENTRIES[0];
              const match: BackfillMatchPayload = {
                slotId: payload.slotId,
                resourceId: payload.resourceId,
                start: payload.start,
                end: payload.end,
                autoNotified: false,
                matches: [
                  {
                    waitlistEntryId: top.id,
                    patientId: top.patientId,
                    displayLabel: top.displayLabel,
                    priority: top.priority,
                    acuityLevel: top.acuityLevel,
                    matchScore: 94,
                    matchReason: ['Urgency priority', 'Morning preference matches slot', 'In-person requested'],
                    offerExpiresAt: new Date(Date.now() + 60_000).toISOString(),
                  },
                ],
              };
              addLog('BACKFILL_MATCH', `Match: ${top.displayLabel} (score 94)`, match);
              resolve(match);
            }
          }, 1_500);
        });
      },

      onBackfillOutcome: (payload: BackfillOutcomePayload) => {
        addLog('BACKFILL_OUTCOME', `Slot ${payload.slotId} — ${payload.outcome}`, payload);
      },

      onWaitlistCommand: (payload: WaitlistCommandPayload) => {
        addLog('WAITLIST_COMMAND', `${payload.intent} — "${payload.raw}"`, payload);
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlistEnabled, waitlistScenario, DEMO_WAITLIST_ENTRIES]);

  // ─── No-Show Risk config ──────────────────────────────────────────────────

  const noShowRiskConfig = useMemo<NoShowRiskConfig | undefined>(() => {
    if (!noShowRiskEnabled) return undefined;

    return {
      enabled: true,

      onRequestRiskScores: (payload: RiskScoresRequestPayload) => {
        addLog('RISK_SCORES_REQUEST', `${payload.appointments.length} appointments`, payload);

        // Build synthetic risk scores based on appointment characteristics.
        return new Promise<{ scores: AppointmentRiskScore[] }>((resolve) => {
          setTimeout(() => {
            const scores: AppointmentRiskScore[] = payload.appointments.map((appt, idx) => {
              const hour = new Date(appt.start).getHours();
              let riskLevel: RiskLevel = 'low';
              let riskScore = 15;
              const riskFactors: string[] = [];

              if (hour < 8 || hour >= 16) {
                riskLevel = 'moderate';
                riskScore = 45;
                riskFactors.push('Inconvenient time slot');
              }
              if (hour < 7) {
                riskLevel = 'high';
                riskScore = 70;
                riskFactors.push('Very early morning appointment');
              }
              if (idx % 7 === 0) {
                riskLevel = 'critical';
                riskScore = 92;
                riskFactors.push('Prior no-show history', 'Long travel distance', 'Multiple reschedules');
              } else if (idx % 4 === 0 && riskLevel === 'low') {
                riskLevel = 'moderate';
                riskScore = 52;
                riskFactors.push('First appointment with provider', 'No prior engagement');
              }

              return {
                appointmentId: appt.appointmentId,
                patientId: appt.patientId,
                resourceId: appt.resourceId,
                appointmentStart: appt.start,
                riskLevel,
                riskScore,
                riskFactors: riskFactors.length ? riskFactors : ['Low baseline risk'],
                suppressReminders: false,
                requireConfirmation: riskLevel === 'critical',
                flagForOutreach: riskLevel === 'critical',
              };
            });

            addLog('RISK_SCORES', `${scores.length} scores (${scores.filter(s => s.riskLevel === 'high' || s.riskLevel === 'critical').length} high/critical)`, scores);
            setCachedRiskScores(scores);
            resolve({ scores });
          }, 300);
        });
      },

      onReminderDue: (payload: ReminderDuePayload) => {
        addLog('REMINDER_DUE',
          `Appt ${payload.appointmentId} — ${payload.riskLevel} risk — reminder ${payload.reminderNumber}/${payload.totalReminders} via ${payload.channel} — ${payload.hoursUntilAppointment}h before`,
          payload,
        );
      },

      onReminderResponse: () => {
        // In a real app this would be driven by the host sending APPOINTMENT_STATUS_UPDATE.
        // The host simulator has no UI for this — just log receipt.
      },

      onRiskCommand: (payload) => {
        addLog('RISK_COMMAND', `${payload.intent} — "${payload.raw}"`, payload);
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noShowRiskEnabled]);

  // ─── Group Therapy config ─────────────────────────────────────────────────

  const DEMO_GROUP_SESSIONS: Record<string, GroupSession> = useMemo(() => {
    const enrolled = (patientId: string, displayLabel: string): GroupEnrollment => ({
      patientId, displayLabel, status: 'enrolled', enrolledAt: '2026-01-15T00:00:00.000Z',
    });
    const waitlisted = (patientId: string, displayLabel: string, pos: number): GroupEnrollment => ({
      patientId, displayLabel, status: 'waitlisted', enrolledAt: '2026-01-20T00:00:00.000Z', waitlistPosition: pos,
    });
    return {
      'grp-mon-dbt': {
        groupId: 'grp-mon-dbt',
        groupName: 'DBT Skills Group',
        groupType: 'DBT',
        facilityType: 'in_person',
        capacity: 12,
        enrolledCount: 9,
        waitlistCount: 2,
        location: 'Room 204 — Main Campus',
        facilitatorIds: ['alice'],
        isOpen: true,
        roster: [
          enrolled('P-1001', 'Maria Santos'),    enrolled('P-1002', 'James Reilly'),
          enrolled('P-1003', 'Alicia Kim'),       enrolled('P-1004', 'David Park'),
          enrolled('P-1005', 'Yolanda Cruz'),     enrolled('P-1006', 'Terrence Hall'),
          enrolled('P-1007', 'Priya Nair'),       enrolled('P-1008', 'Carlos Vega'),
          enrolled('P-1009', 'Bethany Owens'),
          waitlisted('P-2001', 'Franklin Moore', 1), waitlisted('P-2002', 'Natasha Webb', 2),
        ],
      },
      'grp-tue-trauma': {
        groupId: 'grp-tue-trauma',
        groupName: 'Trauma Processing Group',
        groupType: 'trauma',
        facilityType: 'in_person',
        capacity: 8,
        enrolledCount: 8,
        waitlistCount: 3,
        location: 'Room 101 — Annex Building',
        facilitatorIds: ['carol'],
        isOpen: false,
        roster: [
          enrolled('P-1010', 'Lena Fischer'),  enrolled('P-1011', 'Omar Abdullah'),
          enrolled('P-1012', 'Sarah Patel'),   enrolled('P-1013', 'Tomás Herrera'),
          enrolled('P-1014', 'Ingrid Larson'), enrolled('P-1015', 'James Okafor'),
          enrolled('P-1016', 'Hannah Wu'),     enrolled('P-1017', 'Victor Santos'),
          waitlisted('P-2003', 'Angela Moss', 1), waitlisted('P-2004', 'Derek Lin', 2), waitlisted('P-2005', 'Felicia Grant', 3),
        ],
      },
      'grp-wed-anxiety': {
        groupId: 'grp-wed-anxiety',
        groupName: 'Anxiety Management Group',
        groupType: 'anxiety',
        facilityType: 'telehealth',
        capacity: 10,
        enrolledCount: 5,
        waitlistCount: 0,
        location: 'Telehealth — Zoom',
        facilitatorIds: ['eve'],
        isOpen: true,
        roster: [
          enrolled('P-1018', 'Robin Chen'),   enrolled('P-1019', 'Alice Turner'),
          enrolled('P-1020', 'Marcus Bell'),  enrolled('P-1021', 'Diana Reyes'),
          enrolled('P-1022', 'Kwame Asante'),
        ],
      },
      'grp-wed-sub': {
        groupId: 'grp-wed-sub',
        groupName: 'Substance Use Recovery',
        groupType: 'substance_use',
        facilityType: 'in_person',
        capacity: 15,
        enrolledCount: 11,
        waitlistCount: 0,
        location: 'Room 310 — West Wing',
        facilitatorIds: ['bob'],
        isOpen: true,
        roster: [
          enrolled('P-1023', 'Elena Petrov'),    enrolled('P-1024', 'Nathan Brooks'),
          enrolled('P-1025', 'Amara Diallo'),    enrolled('P-1026', "Patrick O'Brien"),
          enrolled('P-1027', 'Mei-Ling Zhang'),  enrolled('P-1028', 'Jamal Thomas'),
          enrolled('P-1029', 'Iris Nakamura'),   enrolled('P-1030', 'Demetrius King'),
          enrolled('P-1031', 'Lydia Johansson'), enrolled('P-1032', 'Rico Martinez'),
          enrolled('P-1033', 'Tanya Fields'),
        ],
      },
      'grp-thu-cbt': {
        groupId: 'grp-thu-cbt',
        groupName: 'CBT Psychoeducation Group',
        groupType: 'CBT',
        facilityType: 'hybrid',
        capacity: 12,
        enrolledCount: 4,
        waitlistCount: 0,
        location: 'Room 106 / Zoom hybrid',
        facilitatorIds: ['henry'],
        isOpen: true,
        roster: [
          enrolled('P-1034', 'Cecilia Romero'), enrolled('P-1035', 'Jerome Watkins'),
          enrolled('P-1036', 'Fatima Hassan'),  enrolled('P-1037', 'Elijah Frost'),
        ],
      },
      'grp-fri-grief': {
        groupId: 'grp-fri-grief',
        groupName: 'Grief & Loss Support Group',
        groupType: 'grief',
        facilityType: 'in_person',
        capacity: 10,
        enrolledCount: 8,
        waitlistCount: 1,
        location: 'Chapel Hall — Ground Floor',
        facilitatorIds: ['iris'],
        isOpen: true,
        roster: [
          enrolled('P-1038', 'Nadia Volkov'),    enrolled('P-1039', 'Samuel Achebe'),
          enrolled('P-1040', 'Grace Sullivan'),   enrolled('P-1041', 'Bo Nguyen'),
          enrolled('P-1042', 'Simone Dupont'),    enrolled('P-1043', 'Ivan Petrov'),
          enrolled('P-1044', 'Clara Mendez'),     enrolled('P-1045', 'Augustus Brown'),
          waitlisted('P-2006', 'Miriam Cohen', 1),
        ],
      },
    };
  }, []);

  // Build session map from all isGroupTherapy events, merging DEMO data and dynamic enrollments.
  const allGroupSessions = useMemo<Record<string, GroupSession>>(() => {
    if (!groupTherapyEnabled) return {};
    const result: Record<string, GroupSession> = {};
    for (const ev of events) {
      if (!ev.isGroupTherapy) continue;
      const extra = demoEnrollments[ev.id] ?? [];
      const base = DEMO_GROUP_SESSIONS[ev.id];
      if (base) {
        // Demo event: DEMO data is the base; event fields override metadata; dynamic enrollments are appended.
        const roster = [...(base.roster ?? []), ...extra];
        const enrolledCount = roster.filter((r) => r.status === 'enrolled').length;
        const waitlistCount = roster.filter((r) => r.status === 'waitlisted').length;
        const capacity = ev.groupCapacity ?? base.capacity;
        result[ev.id] = {
          ...base,
          groupName: ev.title,
          groupType: ev.groupType ?? base.groupType,
          facilityType: (ev.facilityType as GroupSession['facilityType'] | undefined) ?? base.facilityType,
          location: ev.location ?? base.location,
          capacity,
          roster,
          enrolledCount,
          waitlistCount,
          isOpen: enrolledCount < capacity,
        };
      } else {
        // User-created event: build session from event fields and assigned clients.
        const assignedClients = DEMO_CLIENTS.filter((c) => ev.groupClientIds?.includes(c.id));
        const roster = [
          ...assignedClients.map((c) => ({
            patientId: c.id,
            displayLabel: c.name,
            status: 'enrolled' as const,
            enrolledAt: ev.startUtc || new Date().toISOString(),
          })),
          ...extra,
        ];
        const enrolledCount = roster.filter((r) => r.status === 'enrolled').length;
        const capacity = ev.groupCapacity ?? 10;
        result[ev.id] = {
          groupId: ev.id,
          groupName: ev.title,
          groupType: ev.groupType ?? 'psychoeducation',
          facilityType: (ev.facilityType as GroupSession['facilityType'] | undefined) ?? 'in_person',
          location: ev.location,
          capacity,
          enrolledCount,
          waitlistCount: roster.filter((r) => r.status === 'waitlisted').length,
          isOpen: enrolledCount < capacity,
          roster,
        };
      }
    }
    return result;
  }, [groupTherapyEnabled, events, DEMO_GROUP_SESSIONS, demoEnrollments]);

  const groupTherapyConfig = useMemo<GroupTherapyConfig | undefined>(() => {
    if (!groupTherapyEnabled) return undefined;

    return {
      enabled: true,

      onRequestGroupSessions: (payload: GroupSessionsRequestPayload) => {
        addLog('GROUP_SESSIONS_REQUEST', `Range: ${payload.visibleRangeStart} → ${payload.visibleRangeEnd}`, payload);
        return new Promise<{ sessions: Record<string, GroupSession> }>((resolve) => {
          setTimeout(() => {
            resolve({ sessions: allGroupSessions });
            addLog('GROUP_SESSIONS_STATE', `${Object.keys(allGroupSessions).length} group sessions returned`, allGroupSessions);
          }, 600);
        });
      },

      onEnrollRequest: (payload: GroupEnrollRequestPayload) => {
        addLog('GROUP_ENROLL_REQUEST', `Patient ${payload.patientId} → group ${payload.groupId}`, payload);
        const session = allGroupSessions[payload.groupId];

        return new Promise<GroupEnrollOutcomePayload>((resolve) => {
          setTimeout(() => {
            if (!session) {
              const outcome: GroupEnrollOutcomePayload = {
                eventId: payload.eventId,
                groupId: payload.groupId,
                patientId: payload.patientId,
                outcome: 'rejected',
                rejectionReason: 'Group session not found.',
              };
              addLog('GROUP_ENROLL_OUTCOME', 'Rejected — group not found', outcome);
              resolve(outcome);
              return;
            }

            if (!session.isOpen || session.enrolledCount >= session.capacity) {
              const pos = (session.waitlistCount ?? 0) + 1;
              const newEntry: GroupEnrollment = {
                patientId: payload.patientId,
                displayLabel: payload.displayLabel,
                status: 'waitlisted',
                enrolledAt: new Date().toISOString(),
                waitlistPosition: pos,
              };
              setDemoEnrollments((prev) => ({
                ...prev,
                [payload.groupId]: [...(prev[payload.groupId] ?? []), newEntry],
              }));
              const updatedSession: GroupSession = {
                ...session,
                roster: [...(session.roster ?? []), newEntry],
                waitlistCount: (session.waitlistCount ?? 0) + 1,
              };
              const outcome: GroupEnrollOutcomePayload = {
                eventId: payload.eventId,
                groupId: payload.groupId,
                patientId: payload.patientId,
                outcome: 'waitlisted',
                waitlistPosition: pos,
                updatedSession,
              };
              addLog('GROUP_ENROLL_OUTCOME', `Waitlisted at position ${pos}`, outcome);
              resolve(outcome);
              return;
            }

            const newEntry: GroupEnrollment = {
              patientId: payload.patientId,
              displayLabel: payload.displayLabel,
              status: 'enrolled',
              enrolledAt: new Date().toISOString(),
            };
            setDemoEnrollments((prev) => ({
              ...prev,
              [payload.groupId]: [...(prev[payload.groupId] ?? []), newEntry],
            }));
            const updatedSession: GroupSession = {
              ...session,
              roster: [...(session.roster ?? []), newEntry],
              enrolledCount: session.enrolledCount + 1,
              isOpen: (session.enrolledCount + 1) < session.capacity,
            };
            const outcome: GroupEnrollOutcomePayload = {
              eventId: payload.eventId,
              groupId: payload.groupId,
              patientId: payload.patientId,
              outcome: 'enrolled',
              updatedSession,
            };
            addLog('GROUP_ENROLL_OUTCOME', `Enrolled — ${session.groupName}`, outcome);
            resolve(outcome);
          }, 900);
        });
      },

      onRequestAttendance: (payload: GroupAttendanceRequestPayload) => {
        addLog('GROUP_ATTENDANCE_REQUEST', `Group ${payload.groupId}`, payload);
        const session = allGroupSessions[payload.groupId];

        return new Promise<GroupAttendancePayload>((resolve) => {
          setTimeout(() => {
            const fullRoster: GroupEnrollment[] = session?.roster ?? [];
            const enrolledMembers = fullRoster.filter((r) => r.status === 'enrolled');
            const waitlistMembers = fullRoster.filter((r) => r.status === 'waitlisted');
            const cutoff = Math.floor(enrolledMembers.length * 0.8);
            const attendedMembers = enrolledMembers.slice(0, cutoff);
            const noShowMembers = enrolledMembers.slice(cutoff);

            const attendancePayload: GroupAttendancePayload = {
              eventId: payload.eventId,
              groupId: payload.groupId,
              roster: [
                ...attendedMembers.map((r): GroupEnrollment => ({ ...r, status: 'attended', attendedAt: new Date().toISOString() })),
                ...noShowMembers.map((r): GroupEnrollment => ({ ...r, status: 'no_show' })),
                ...waitlistMembers,
              ],
              attendanceSummary: {
                enrolled: 0,
                attended: attendedMembers.length,
                noShow: noShowMembers.length,
                cancelled: 0,
              },
            };
            addLog('GROUP_ATTENDANCE', `${attendedMembers.length} attended / ${noShowMembers.length} no-show`, attendancePayload);
            resolve(attendancePayload);
          }, 700);
        });
      },

      onCheckConflict: (payload: GroupConflictCheckPayload) => {
        addLog('GROUP_CONFLICT_CHECK', `Patient ${payload.patientId} — group ${payload.groupId}`, payload);

        return new Promise<GroupConflictResultPayload>((resolve) => {
          setTimeout(() => {
            const existing = events.find(
              (ev) =>
                !GROUP_EVENT_IDS.includes(ev.id as typeof GROUP_EVENT_IDS[number]) &&
                ev.resourceId === payload.patientId &&
                new Date(ev.startUtc) < new Date(payload.end) &&
                new Date(ev.endUtc) > new Date(payload.start),
            );

            const result: GroupConflictResultPayload = existing
              ? {
                  eventId: payload.eventId,
                  patientId: payload.patientId,
                  hasConflict: true,
                  severity: 'soft',
                  conflicts: [{
                    eventId: existing.id,
                    eventTitle: existing.title,
                    start: existing.startUtc,
                    end: existing.endUtc,
                    resourceId: existing.resourceId,
                  } as GroupConflict],
                }
              : {
                  eventId: payload.eventId,
                  patientId: payload.patientId,
                  hasConflict: false,
                  severity: 'none',
                  conflicts: [],
                };

            addLog('GROUP_CONFLICT_RESULT', result.hasConflict ? `soft conflict with "${result.conflicts[0]?.eventTitle}"` : 'No conflict', result);
            resolve(result);
          }, 400);
        });
      },

      onGroupCommand: (payload: GroupCommandPayload) => {
        addLog('GROUP_COMMAND', `${payload.intent} — "${payload.raw}"`, payload);
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupTherapyEnabled, allGroupSessions, events]);

  const tvModeConfig = useMemo<TvModeConfig | undefined>(() => {
    if (!tvModeEnabled) return undefined;
    const selectedIds = tvResourceIds.size > 0 ? [...tvResourceIds] : undefined;
    return {
      enabled: true,
      logoUrl: tvLogoUrl.trim() || undefined,
      tvResources: selectedIds,
      resourcesPerPage: tvResourcesPerPage,
      autoRotateMs: tvAutoRotateSec * 1000,
    };
  }, [tvModeEnabled, tvLogoUrl, tvResourceIds, tvResourcesPerPage, tvAutoRotateSec]);

  const effectiveConfig = useMemo<ScheduleConfig>(() => ({
    ...config,
    resources: showResources
      ? resources.filter((r) => filteredResourceIds.has(r.id))
      : undefined,
    resourcesPerPage: showResources ? resourcesPerPage : undefined,
    eventIcon: showEventIcons ? eventPriorityIcon : undefined,
    resourcePanel: showResources ? resourcePanelConfig : undefined,
    holidays: activeHolidays.length ? activeHolidays : undefined,
    schedulingQueue: tvModeEnabled ? undefined : schedulingQueueConfig,
    propertiesPanel: tvModeEnabled ? undefined : propertiesPanelConfig,
    validation: Object.values(validation).some(Boolean) ? validation : undefined,
    tvMode: tvModeConfig,
    aiChatPanel: tvModeEnabled ? undefined : aiChatPanelConfig,
    acuitySlots: acuityConfig,
    doubleClickForm: useCustomDoubleClickForm ? 'host' : undefined,
    clients: DEMO_CLIENTS,
  }), [config, showResources, resources, filteredResourceIds, resourcesPerPage, showEventIcons, resourcePanelConfig, activeHolidays, schedulingQueueConfig, propertiesPanelConfig, validation, tvModeEnabled, tvModeConfig, aiChatPanelConfig, acuityConfig, useCustomDoubleClickForm, groupTherapyEnabled]);

  const effectiveEvents = useMemo<ScheduleEvent[]>(() => {
    if (!showResources) return events;
    // Wait list events bypass resource filtering — they live in the panel, not the grid.
    return events.filter((e) => e.queued || !e.resourceId || filteredResourceIds.has(e.resourceId));
  }, [events, showResources, filteredResourceIds]);

  const LOG_TYPE_COLORS: Record<string, string> = {
    REQUEST_DATA: '#60a5fa',
    EVENT_MOVED: '#34d399',
    EVENT_RESIZED: '#a78bfa',
    EVENT_SAVED: '#86efac',
    EVENT_DELETED: '#f87171',
    EVENT_CREATED: '#fbbf24',
    SLOT_CLICK: '#94a3b8',
    VIEW_CHANGED: '#fb923c',
    DATE_NAVIGATED: '#e879f9',
    SSE_ADD: '#22c55e',
    SSE_UPDATE: '#38bdf8',
    SSE_REMOVE: '#fb7185',
    WAITLIST_SELECTED: '#c084fc',
    WAITLIST_SCHEDULED: '#4ade80',
    VALIDATION_ERROR: '#f87171',
    EVENT_CLICK: '#38bdf8',
    EVENT_DOUBLE_CLICK: '#0ea5e9',
    NEW_EVENT_INTERCEPTED: '#38bdf8',
    TV_PAGE_CHANGED: '#f472b6',
    AI_MESSAGE: '#a78bfa',
    // Screener Gate
    SLOT_SELECTED: '#38bdf8',
    SCREENER_COMPLETE: '#fb923c',
    APPOINTMENT_BOOKED: '#4ade80',
    // Acuity Slot Reservation
    ACUITY_SLOTS_REQUEST: '#7dd3fc',
    ACUITY_SLOTS_CONFIG: '#6ee7b7',
    ACUITY_RELEASE_WARNING: '#fbbf24',
    ACUITY_SLOT_CLAIMED: '#4ade80',
    ACUITY_COMMAND: '#c084fc',
    // Provider Matching
    MATCHING_REQUEST: '#86efac',
    MATCHING_RESULTS: '#4ade80',
    MATCHING_COMMAND: '#a78bfa',
    PROVIDER_SELECTED: '#34d399',
    MATCHING_CLEARED: '#94a3b8',
    // Waitlist / Auto-Backfill
    WAITLIST_STATE_REQUEST: '#a5f3fc',
    WAITLIST_STATE: '#67e8f9',
    SLOT_OPENED: '#fde68a',
    BACKFILL_MATCH: '#4ade80',
    BACKFILL_NO_MATCH: '#f87171',
    BACKFILL_OUTCOME: '#86efac',
    WAITLIST_COMMAND: '#c084fc',
    // No-Show Risk
    RISK_SCORES_REQUEST: '#fda4af',
    RISK_SCORES: '#f87171',
    REMINDER_DUE: '#fb923c',
    OUTREACH_FLAGGED: '#fbbf24',
    RISK_COMMAND: '#a78bfa',
    // Group Therapy
    GROUP_SESSIONS_REQUEST: '#a5b4fc',
    GROUP_SESSIONS_STATE: '#818cf8',
    GROUP_ENROLL_REQUEST: '#6ee7b7',
    GROUP_ENROLL_OUTCOME: '#4ade80',
    GROUP_ATTENDANCE_REQUEST: '#fde68a',
    GROUP_ATTENDANCE: '#fbbf24',
    GROUP_CONFLICT_CHECK: '#fca5a5',
    GROUP_CONFLICT_RESULT: '#f87171',
    GROUP_COMMAND: '#c084fc',
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-logo">
          <button
            className="hamburger-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            aria-label="Toggle configuration panel"
          >
            <span /><span /><span />
          </button>
          <span className="app-logo-icon">📅</span>
          <span className="app-logo-text">Fulcrum Schedule</span>
          <span className="app-logo-badge">Example Host App</span>
        </div>
        <div className="app-header-right">
          <span className="app-subtitle">
            Demonstrating <code>@fulcrum/schedule-embedded</code>
          </span>
          <span className="app-header-sep" />
          <a
            className="app-header-link"
            href="https://www.telerik.com/kendo-react-ui"
            target="_blank"
            rel="noopener noreferrer"
          >
            Powered by KendoReact
          </a>
          <span className="app-header-sep" />
          <a
            className="app-header-link app-header-link--github"
            href="https://github.com/srupke/Fulcrum.Schedule.Embedded"
            target="_blank"
            rel="noopener noreferrer"
            title="View source on GitHub"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" fill="currentColor">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
            </svg>
            GitHub
          </a>
        </div>
      </header>

      <div className="app-body">
        {!sidebarCollapsed && (
          <div className="sidebar-backdrop" onClick={() => setSidebarCollapsed(true)} />
        )}
        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          className={`sidebar${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}
          style={sidebarCollapsed ? undefined : { width: sidebarWidth }}
        >
          {sidebarCollapsed ? (
            <button
              className="sidebar-expand-btn"
              onClick={() => setSidebarCollapsed(false)}
              title="Expand sidebar"
            >
              ›
            </button>
          ) : (
            <>
              <div className="sidebar-tabs">
                <button
                  className={`sidebar-tab ${sidebarTab === 'config' ? 'active' : ''}`}
                  onClick={() => setSidebarTab('config')}
                >
                  Config
                </button>
                <button
                  className={`sidebar-tab ${sidebarTab === 'log' ? 'active' : ''}`}
                  onClick={() => setSidebarTab('log')}
                >
                  Event Log
                  {log.length > 0 && <span className="log-badge">{log.length}</span>}
                </button>
                <button
                  className="sidebar-collapse-btn"
                  onClick={() => setSidebarCollapsed(true)}
                  title="Collapse sidebar"
                >
                  ‹
                </button>
              </div>

              {sidebarTab === 'config' && (
                <div className="config-panel">
                  <div className="config-strip">
                    <div className="config-strip-row">
                      <select
                        className="config-strip-select"
                        value={activeConfigId ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) setActiveConfigId(null);
                          else handleLoadConfig(val);
                        }}
                      >
                        <option value="">— no saved config —</option>
                        {savedConfigs.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="config-strip-row">
                      <button
                        className="config-strip-btn config-strip-btn-primary"
                        disabled={!activeConfigId}
                        onClick={handleSave}
                        title="Update saved config with current settings"
                      >
                        Save
                      </button>
                      <button
                        className="config-strip-btn config-strip-btn-ghost"
                        onClick={() => { setShowSaveAsInput((v) => !v); setSaveAsName(''); }}
                        title="Save current settings as a new named config"
                      >
                        Save As
                      </button>
                      <button
                        className="config-strip-btn config-strip-btn-danger"
                        disabled={!activeConfigId}
                        onClick={handleDelete}
                        title="Delete this saved config"
                      >
                        Del
                      </button>
                    </div>
                    {showSaveAsInput && (
                      <div className="config-strip-row">
                        <input
                          className="ctrl-input"
                          placeholder="Config name…"
                          value={saveAsName}
                          onChange={(e) => setSaveAsName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveAs();
                            if (e.key === 'Escape') setShowSaveAsInput(false);
                          }}
                          autoFocus
                        />
                        <button
                          className="config-strip-btn config-strip-btn-primary"
                          disabled={!saveAsName.trim()}
                          onClick={handleSaveAs}
                        >
                          OK
                        </button>
                        <button
                          className="config-strip-btn config-strip-btn-ghost"
                          onClick={() => setShowSaveAsInput(false)}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    {activeConfigId && (
                      <div className="config-strip-meta">
                        Saved {new Date(savedConfigs.find((c) => c.id === activeConfigId)!.savedAt).toLocaleString()}
                      </div>
                    )}
                  </div>

                  <div className="config-collapse-bar">
                    <button className="config-collapse-bar-btn" onClick={() => setSectionSignal({ collapsed: true })}>Collapse All</button>
                    <button className="config-collapse-bar-btn" onClick={() => setSectionSignal({ collapsed: false })}>Expand All</button>
                  </div>

                  {/* ── Industry Demo ── */}
                  <ConfigSection title="Industry Demo" signal={sectionSignal}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.6 }}>
                      <strong style={{ color: '#8B5CF6' }}>Behavioral Health — Therapy Practice</strong><br />
                      Events: Therapy Sessions &amp; Intakes<br />
                      Resources: 10 Therapists (LPC, LCSW, LMFT, PhD)<br />
                      Specialties: Trauma, Anxiety, Addiction, Family, OCD…<br />
                      Fields: Client, Session Type, Modality, Visit Type
                    </div>
                    <button
                      className="ctrl-btn ctrl-btn-ghost"
                      onClick={resetBhData}
                      title="Reload the default behavioral health providers and sessions"
                    >
                      Reset Demo Data
                    </button>
                  </ConfigSection>

                  {/* ── TV Mode ── */}
                  <ConfigSection title="TV Mode" signal={sectionSignal}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Display-board mode: hides all navigation and auto-cycles through resources on a timer.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={tvModeEnabled}
                        onChange={(e) => setTvModeEnabled(e.target.checked)}
                      />
                      Enable TV Mode
                    </label>

                    {tvModeEnabled && (
                      <>
                        <div className="ctrl-row">
                          <label>Logo URL</label>
                          <input
                            type="text"
                            className="ctrl-input"
                            value={tvLogoUrl}
                            onChange={(e) => setTvLogoUrl(e.target.value)}
                            placeholder="https://example.com/logo.png"
                          />
                        </div>
                        <div className="ctrl-row">
                          <label>Header Text</label>
                          <input
                            type="text"
                            className="ctrl-input"
                            value={config.headerText ?? ''}
                            onChange={(e) => updateConfig('headerText', e.target.value || undefined)}
                            placeholder="e.g. Aurora Health Care"
                          />
                        </div>
                        <div className="ctrl-row">
                          <label>Resources / Page</label>
                          <input
                            type="number"
                            className="ctrl-input"
                            min={1}
                            max={20}
                            value={tvResourcesPerPage}
                            onChange={(e) => setTvResourcesPerPage(Math.max(1, parseInt(e.target.value) || 4))}
                          />
                        </div>
                        <div className="ctrl-row">
                          <label>Rotate Every (s)</label>
                          <input
                            type="number"
                            className="ctrl-input"
                            min={1}
                            max={300}
                            value={tvAutoRotateSec}
                            onChange={(e) => setTvAutoRotateSec(Math.max(1, parseInt(e.target.value) || 15))}
                          />
                        </div>

                        {showResources && resources.length > 0 && (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>
                              TV Resources (leave all unchecked to use current visible resources)
                            </div>
                            {resources.filter((r) => filteredResourceIds.has(r.id)).map((r) => (
                              <label key={r.id} className="ctrl-check" style={{ fontSize: 11 }}>
                                <input
                                  type="checkbox"
                                  checked={tvResourceIds.has(r.id)}
                                  onChange={(e) => {
                                    setTvResourceIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(r.id);
                                      else next.delete(r.id);
                                      return next;
                                    });
                                  }}
                                />
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {r.color && (
                                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                                  )}
                                  {r.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {tvLogoUrl.trim() && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Logo preview:</div>
                            <img
                              src={tvLogoUrl}
                              alt="Logo preview"
                              style={{ maxHeight: 40, maxWidth: '100%', objectFit: 'contain', background: '#1e293b', borderRadius: 4, padding: 4 }}
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </ConfigSection>

                  <ConfigGroup title="Display">

                  {/* ── View ── */}
                  <ConfigSection title="View" signal={sectionSignal}>
                    <select
                      className="ctrl-select"
                      value={config.view}
                      onChange={(e) => updateConfig('view', e.target.value as ViewType)}
                    >
                      <option value="day">Day</option>
                      <option value="weekdays">Work Week</option>
                      <option value="fullWeek">Week</option>
                      <option value="month">Month</option>
                      <option value="agenda">Agenda</option>
                    </select>
                    <div className="ctrl-row">
                      <label>Agenda Days</label>
                      <input
                        type="number"
                        className="ctrl-input"
                        min={1}
                        max={365}
                        value={config.agendaDays ?? 30}
                        onChange={(e) => updateConfig('agendaDays', Math.max(1, parseInt(e.target.value) || 30))}
                      />
                    </div>
                    <div className="ctrl-row">
                      <label>Agenda Group Date</label>
                      <select
                        className="ctrl-select"
                        value={agendaGroupFormatIndex}
                        onChange={(e) => updateAgendaGroupFormat(parseInt(e.target.value))}
                      >
                        {AGENDA_GROUP_FORMAT_PRESETS.map((p, i) => (
                          <option key={i} value={i}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                  </ConfigSection>

                  {/* ── Time Range ── */}
                  <ConfigSection title="Time Range" signal={sectionSignal}>
                    <div className="ctrl-row">
                      <label>Start</label>
                      <input
                        type="time"
                        className="ctrl-input"
                        value={config.timeRange.start}
                        onChange={(e) =>
                          updateConfig('timeRange', { ...config.timeRange, start: e.target.value })
                        }
                      />
                    </div>
                    <div className="ctrl-row">
                      <label>End</label>
                      <input
                        type="time"
                        className="ctrl-input"
                        value={config.timeRange.end}
                        onChange={(e) =>
                          updateConfig('timeRange', { ...config.timeRange, end: e.target.value })
                        }
                      />
                    </div>
                  </ConfigSection>

                  {/* ── Slot Duration ── */}
                  <ConfigSection title="Slot Duration" signal={sectionSignal}>
                    <select
                      className="ctrl-select"
                      value={config.slotDuration}
                      onChange={(e) => updateConfig('slotDuration', parseInt(e.target.value) as SlotDuration)}
                    >
                      <option value="5">5 minutes</option>
                      <option value="10">10 minutes</option>
                      <option value="15">15 minutes</option>
                      <option value="20">20 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1½ hours</option>
                      <option value="120">2 hours</option>
                    </select>
                  </ConfigSection>

                  {/* ── Zoom Controls ── */}
                  <ConfigSection title="Zoom Controls" signal={sectionSignal}>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.zoom?.show ?? false}
                        onChange={(e) =>
                          updateConfig('zoom', { ...config.zoom, show: e.target.checked } as ZoomConfig)
                        }
                      />
                      Show zoom buttons in toolbar
                    </label>
                    <div className="ctrl-row">
                      <label>Min (min)</label>
                      <input
                        type="number"
                        className="ctrl-input"
                        min={5}
                        max={55}
                        step={5}
                        disabled={!config.zoom?.show}
                        value={config.zoom?.min ?? 5}
                        onChange={(e) =>
                          updateConfig('zoom', {
                            ...config.zoom,
                            min: Math.max(5, Math.min(55, parseInt(e.target.value) || 5)),
                          } as ZoomConfig)
                        }
                      />
                    </div>
                    <div className="ctrl-row">
                      <label>Max (min)</label>
                      <input
                        type="number"
                        className="ctrl-input"
                        min={10}
                        max={60}
                        step={5}
                        disabled={!config.zoom?.show}
                        value={config.zoom?.max ?? 60}
                        onChange={(e) =>
                          updateConfig('zoom', {
                            ...config.zoom,
                            max: Math.max(10, Math.min(60, parseInt(e.target.value) || 60)),
                          } as ZoomConfig)
                        }
                      />
                    </div>
                  </ConfigSection>

                  {/* ── Timezone ── */}
                  <ConfigSection title="Timezone" signal={sectionSignal}>
                    <select
                      className="ctrl-select"
                      value={config.timezone}
                      onChange={(e) => updateConfig('timezone', e.target.value)}
                    >
                      {US_TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label} ({tz.value.split('/')[1]})
                        </option>
                      ))}
                    </select>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.observeDaylightSaving !== false}
                        onChange={(e) => updateConfig('observeDaylightSaving', e.target.checked)}
                      />
                      Observe Daylight Saving Time
                    </label>
                  </ConfigSection>

                  {/* ── Time Format ── */}
                  <ConfigSection title="Time Format" signal={sectionSignal}>
                    <select
                      className="ctrl-select"
                      value={config.timeFormat ?? '12h'}
                      onChange={(e) => updateConfig('timeFormat', e.target.value as TimeFormat)}
                    >
                      {TIME_FORMAT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </ConfigSection>

                  {/* ── Additional Time Zones ── */}
                  <ConfigSection title="Additional Time Zones" signal={sectionSignal}>
                    {(config.secondaryTimezones ?? []).map((st, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <select
                            className="ctrl-select"
                            style={{ flex: 1, width: 'auto', minWidth: 0 }}
                            value={st.timezone}
                            onChange={(e) => {
                              const next: SecondaryTimezone[] = [...(config.secondaryTimezones ?? [])];
                              next[i] = { ...next[i], timezone: e.target.value };
                              updateConfig('secondaryTimezones', next);
                            }}
                          >
                            {WORLD_TIMEZONES.map((tz) => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                          </select>
                          <button
                            className="ctrl-btn ctrl-btn-ghost"
                            style={{ padding: '3px 7px', fontSize: 12, flexShrink: 0, width: 'auto' }}
                            onClick={() => {
                              const next = (config.secondaryTimezones ?? []).filter((_, j) => j !== i);
                              updateConfig('secondaryTimezones', next.length ? next : undefined);
                            }}
                          >✕</button>
                        </div>
                        <input
                          type="text"
                          className="ctrl-input"
                          placeholder="Label override (e.g. NYC)"
                          value={st.label ?? ''}
                          onChange={(e) => {
                            const next: SecondaryTimezone[] = [...(config.secondaryTimezones ?? [])];
                            next[i] = { ...next[i], label: e.target.value || undefined };
                            updateConfig('secondaryTimezones', next);
                          }}
                        />
                      </div>
                    ))}
                    <button
                      className="ctrl-btn"
                      style={{ width: '100%' }}
                      onClick={() =>
                        updateConfig('secondaryTimezones', [
                          ...(config.secondaryTimezones ?? []),
                          { timezone: 'America/New_York' },
                        ])
                      }
                    >
                      + Add Time Zone
                    </button>
                  </ConfigSection>

                  </ConfigGroup>

                  {/* ── Resources (conditional) ── */}
                  {showResources && (
                    <ConfigGroup title="Resources">
                      <ConfigSection title="Filter Resources" signal={sectionSignal}>
                        {resources.map((r) => (
                          <label key={r.id} className="ctrl-check">
                            <input
                              type="checkbox"
                              checked={filteredResourceIds.has(r.id)}
                              onChange={(e) => toggleResource(r.id, e.target.checked)}
                            />
                            <span
                              className="resource-dot"
                              style={{ background: r.color ?? '#64748b' }}
                            />
                            {r.name}
                          </label>
                        ))}
                      </ConfigSection>

                      <ConfigSection title="Resource Grouping" signal={sectionSignal}>
                        <select
                          className="ctrl-select"
                          value={config.resourceGrouping ?? 'byDate'}
                          onChange={(e) => updateConfig('resourceGrouping', e.target.value as 'byDate' | 'byResource')}
                        >
                          <option value="byDate">Group by Date (resources as columns)</option>
                          <option value="byResource">Group by Resource (resources as rows)</option>
                        </select>
                        {(config.resourceGrouping ?? 'byDate') === 'byDate' && (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Columns per page</div>
                            {([
                              { view: 'day',      label: 'Day' },
                              { view: 'weekdays', label: 'Work Week' },
                              { view: 'fullWeek', label: 'Week' },
                              { view: 'month',    label: 'Month' },
                            ] as { view: ViewType; label: string }[]).map(({ view: v, label }) => (
                              <div key={v} className="ctrl-row">
                                <label>{label}</label>
                                <select
                                  className="ctrl-select"
                                  style={{ width: 'auto' }}
                                  value={resourcesPerPage[v] ?? 2}
                                  onChange={(e) =>
                                    setResourcesPerPage((prev) => ({ ...prev, [v]: parseInt(e.target.value) }))
                                  }
                                >
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, marginBottom: 4 }}>Resources per page</div>
                            <div className="ctrl-row">
                              <label>Agenda</label>
                              <select
                                className="ctrl-select"
                                style={{ width: 'auto' }}
                                value={resourcesPerPage['agenda'] ?? 2}
                                onChange={(e) =>
                                  setResourcesPerPage((prev) => ({ ...prev, agenda: parseInt(e.target.value) }))
                                }
                              >
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <option key={n} value={n}>{n}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </ConfigSection>

                      <ConfigSection title="Resource Panel" signal={sectionSignal}>
                        <label className="ctrl-check">
                          <input
                            type="checkbox"
                            checked={showResourcePanel}
                            onChange={(e) => setShowResourcePanel(e.target.checked)}
                          />
                          Enable search panel
                        </label>
                        {showResourcePanel && (
                          <>
                            <div className="ctrl-row">
                              <label>Label</label>
                              <input
                                type="text"
                                className="ctrl-input"
                                placeholder="e.g. Team Members"
                                value={resourcePanelLabel}
                                onChange={(e) => setResourcePanelLabel(e.target.value)}
                              />
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, marginTop: 2 }}>
                              Columns shown: <strong style={{ color: '#94a3b8' }}>Dept · Role</strong>
                              <br />Click any row to jump to that resource.
                            </div>
                          </>
                        )}
                      </ConfigSection>

                      <ConfigSection title="Resource Hours" signal={sectionSignal}>
                        <div className="resource-hours-editor">
                          {resources.map((resource) => (
                            <ResourceHoursEditor
                              key={resource.id}
                              resource={resource}
                              onChange={updateResource}
                            />
                          ))}
                        </div>
                      </ConfigSection>
                    </ConfigGroup>
                  )}

                  <ConfigGroup title="Panels">

                  {/* ── Scheduling Queue ── */}
                  <ConfigSection title="Scheduling Queue" signal={sectionSignal}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      A right-side panel for unscheduled events queued for placement.
                      Drag any card onto the calendar to schedule it.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={showSchedulingQueue}
                        onChange={(e) => setShowSchedulingQueue(e.target.checked)}
                      />
                      Show scheduling queue
                    </label>
                    {showSchedulingQueue && (
                      <div className="ctrl-row" style={{ marginTop: 6 }}>
                        <label>Label</label>
                        <input
                          type="text"
                          className="ctrl-input"
                          placeholder="e.g. Queue"
                          value={schedulingQueueLabel}
                          onChange={(e) => setSchedulingQueueLabel(e.target.value)}
                        />
                      </div>
                    )}
                  </ConfigSection>

                  {/* ── Properties Panel ── */}
                  <ConfigSection title="Properties Panel" signal={sectionSignal}>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={showPropertiesPanel}
                        onChange={(e) => setShowPropertiesPanel(e.target.checked)}
                      />
                      Show properties panel
                    </label>
                    {showPropertiesPanel && (
                      <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, marginTop: 2 }}>
                        Click any event to view its details in the panel.
                        <br />The edit form is suppressed while the panel is active.
                        <br />Look for <code>EVENT_CLICK</code> entries in the log.
                      </div>
                    )}
                  </ConfigSection>

                  {/* ── AI Assistant ── */}
                  <ConfigSection title="AI Assistant" signal={sectionSignal}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Natural language schedule control powered by Claude.
                      Chat to change views, filter providers, set time ranges, and navigate dates.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={showAiChatPanel}
                        onChange={(e) => setShowAiChatPanel(e.target.checked)}
                      />
                      Show AI Assistant panel
                    </label>

                    {showAiChatPanel && (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                            Claude API Key
                          </div>
                          <input
                            type="password"
                            className="ctrl-input"
                            placeholder="sk-ant-api03-…"
                            value={aiApiKey}
                            onChange={(e) => {
                              setAiApiKey(e.target.value);
                              localStorage.setItem('fulcrum-ai-api-key', e.target.value);
                            }}
                            autoComplete="off"
                          />
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                            Key is stored in <code>localStorage</code> (this browser only).
                            <br />Alternatively, set <code>VITE_ANTHROPIC_API_KEY</code> in
                            <br /><code>packages/host/.env.local</code> to avoid entering it here.
                          </div>
                        </div>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Example requests:</div>
                          <div>• "Show Dr. Frazier's next 5 Fridays"</div>
                          <div>• "Afternoon only (1pm–5pm)"</div>
                          <div>• "Switch to week view"</div>
                          <div>• "Show only Dr. Smith"</div>
                          <div>• "Show everyone"</div>
                          <div>• "Morning hours 8am to noon"</div>
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  </ConfigGroup>

                  <ConfigGroup title="Behavioral Health" bh>

                  {/* ── Intake Screener Gate ── */}
                  <ConfigSection title="Screener Gate" signal={sectionSignal} bh>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Intercept slot selection with a clinical intake screener.
                      The host scores responses and routes the booking.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={screenerEnabled}
                        onChange={(e) => setScreenerEnabled(e.target.checked)}
                      />
                      Enable Intake Screener Gate
                    </label>

                    {screenerEnabled && (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                            Screener
                          </div>
                          {(['PHQ9', 'GAD7', 'CSSRS', 'AUDIT_C', 'DAST10', 'skip'] as const).map((s) => (
                            <label key={s} className="ctrl-check" style={{ display: 'block', marginBottom: 2 }}>
                              <input
                                type="radio"
                                name="screener-scenario"
                                checked={screenerScenario === s}
                                onChange={() => setScreenerScenario(s)}
                              />
                              {s === 'PHQ9' && 'PHQ-9 (Depression, 9 Qs)'}
                              {s === 'GAD7' && 'GAD-7 (Anxiety, 7 Qs)'}
                              {s === 'CSSRS' && 'C-SSRS (Suicide Risk, 6 Qs)'}
                              {s === 'AUDIT_C' && 'AUDIT-C (Alcohol Use, 3 Qs)'}
                              {s === 'DAST10' && 'DAST-10 (Drug Use, 10 Qs)'}
                              {s === 'skip' && 'Skip (no screener)'}
                            </label>
                          ))}
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                            Host routing outcome
                          </div>
                          {(['confirm', 'adjust', 'crisis'] as const).map((o) => (
                            <label key={o} className="ctrl-check" style={{ display: 'block', marginBottom: 2 }}>
                              <input
                                type="radio"
                                name="screener-outcome"
                                checked={screenerOutcome === o}
                                onChange={() => setScreenerOutcome(o)}
                              />
                              {o === 'confirm' && 'CONFIRM — book as-is'}
                              {o === 'adjust' && 'ADJUST_SLOT — extend to 90 min'}
                              {o === 'crisis' && 'CRISIS_ROUTE — show resources'}
                            </label>
                          ))}
                        </div>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>How to demo:</div>
                          <div>1. Enable the gate above</div>
                          <div>2. Click any empty slot on the calendar</div>
                          <div>3. The screener dialog opens — answer the questions</div>
                          <div>4. The host applies the selected outcome</div>
                          <div>5. Watch log entries for SLOT_SELECTED,</div>
                          <div>&nbsp;&nbsp;&nbsp;SCREENER_COMPLETE, APPOINTMENT_BOOKED</div>
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  {/* ── Acuity Slot Reservation ── */}
                  <ConfigSection title="Acuity Slots" signal={sectionSignal} bh>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Reserve calendar slots for high-acuity patients. Slots render with
                      level-coded colors and auto-release on a configurable countdown.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={acuityEnabled}
                        onChange={(e) => setAcuityEnabled(e.target.checked)}
                      />
                      Enable Acuity Slot Reservation
                    </label>

                    {acuityEnabled && (
                      <>
                        <label className="ctrl-check" style={{ marginTop: 8 }}>
                          <input
                            type="checkbox"
                            checked={acuityPrivilegedView}
                            onChange={(e) => setAcuityPrivilegedView(e.target.checked)}
                          />
                          Privileged view (show all reserved slots)
                        </label>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Demo reservations:</div>
                          <div>• <span style={{ color: '#fca5a5' }}>Urgent</span> — Crisis follow-up, hidden from general</div>
                          <div>• <span style={{ color: '#fcd34d' }}>High</span> — Post-discharge, locked with tooltip</div>
                          <div>• <span style={{ color: '#93c5fd' }}>Routine</span> — Releases in ~25 min (chip visible)</div>
                          <div style={{ marginTop: 6, color: '#64748b' }}>Reservations are placed at 9 AM, 11 AM, and 2 PM on the first visible day. Enable Resources to see them per-provider.</div>
                        </div>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>How to demo:</div>
                          <div>1. Enable above — 3 slots appear on the calendar</div>
                          <div>2. Hover the locked High slot for the tooltip</div>
                          <div>3. The Routine slot shows a release countdown chip</div>
                          <div>4. Toggle Privileged view to reveal hidden Urgent slot</div>
                          <div>5. In privileged view, click a reserved slot to claim it</div>
                          <div>6. Navigate to refresh reservations for the new range</div>
                          {showAiChatPanel && (
                            <div style={{ marginTop: 4, color: '#a78bfa' }}>
                              7. Try asking the AI: "Hold 2 urgent slots for tomorrow"
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  {/* ── Provider Matching ── */}
                  <ConfigSection title="Provider Matching" signal={sectionSignal} bh>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Filter and rank providers for a patient context.
                      The host scores providers; the component renders matched results
                      in the resource panel. Requires the resource panel to be visible.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={matchingEnabled}
                        onChange={(e) => {
                          setMatchingEnabled(e.target.checked);
                          if (e.target.checked) {
                            setShowResources(true);
                            setShowResourcePanel(true);
                            setFilteredResourceIds(new Set(resources.map((r) => r.id)));
                          }
                        }}
                      />
                      Enable Provider Matching
                    </label>

                    {matchingEnabled && (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                            Patient scenario
                          </div>
                          {([
                            ['trauma-spanish', 'Trauma specialist, Spanish-speaking'],
                            ['cbt-cigna',      'CBT or DBT, Cigna in-network'],
                            ['telehealth-adolescent', 'Telehealth, adolescent specialty'],
                            ['mandarin',       'Mandarin-speaking provider'],
                          ] as const).map(([key, label]) => (
                            <label key={key} className="ctrl-check" style={{ display: 'block', marginBottom: 2 }}>
                              <input
                                type="radio"
                                name="matching-scenario"
                                checked={matchingScenario === key}
                                onChange={() => setMatchingScenario(key)}
                              />
                              {label}
                            </label>
                          ))}
                        </div>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>How to demo:</div>
                          <div>1. Enable matching above — resource panel auto-opens</div>
                          <div>2. Select a patient scenario to fire MATCHING_REQUEST</div>
                          <div>3. Providers rank by score in the resource panel:</div>
                          <div>&nbsp;&nbsp;● Green border = strong match (≥80)</div>
                          <div>&nbsp;&nbsp;◐ Amber border = partial match (50–79)</div>
                          <div>&nbsp;&nbsp;○ Gray border = weak match (30–49)</div>
                          <div>4. Click a provider → PROVIDER_SELECTED fires</div>
                          <div>5. Collapse section shows "X other providers"</div>
                          <div>6. Clear × in the banner resets to standard view</div>
                          {showAiChatPanel && (
                            <div style={{ marginTop: 4, color: '#a78bfa' }}>
                              7. Try: "Find a Spanish-speaking CBT provider" or<br />
                              &nbsp;&nbsp;&nbsp;"Show telehealth providers available Thursday"
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  {/* ── Waitlist / Auto-Backfill ── */}
                  <ConfigSection title="Waitlist / Auto-Backfill" signal={sectionSignal} bh>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Prioritized patient waitlist with automatic slot-opening detection.
                      The host owns all PHI — the component is a trigger and renderer only.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={waitlistEnabled}
                        onChange={(e) => setWaitlistEnabled(e.target.checked)}
                      />
                      Enable Waitlist / Auto-Backfill
                    </label>

                    {waitlistEnabled && (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                            Backfill demo outcome
                          </div>
                          {([
                            ['match',    'BACKFILL_MATCH — top candidate returned (score 94)'],
                            ['no-match', 'BACKFILL_NO_MATCH — no preference match'],
                          ] as const).map(([key, label]) => (
                            <label key={key} className="ctrl-check" style={{ display: 'block', marginBottom: 2 }}>
                              <input
                                type="radio"
                                name="waitlist-scenario"
                                checked={waitlistScenario === key}
                                onChange={() => setWaitlistScenario(key)}
                              />
                              {label}
                            </label>
                          ))}
                        </div>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Demo waitlist:</div>
                          <div>• <span style={{ color: '#fca5a5' }}>2 urgent</span> — crisis follow-up, telehealth</div>
                          <div>• <span style={{ color: '#fcd34d' }}>2 high</span> — trauma, in-person</div>
                          <div>• <span style={{ color: '#93c5fd' }}>1 routine</span> — afternoon telehealth</div>
                          <div style={{ marginTop: 4, color: '#64748b' }}>Oldest wait: 42 days · Est. avg: 18 days</div>
                        </div>

                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>How to demo:</div>
                          <div>1. Enable above — "Waitlist" tab appears in right panel</div>
                          <div>2. Waitlist loads with 5 mock patients (2 urgent)</div>
                          <div>3. Delete or cancel any event on the calendar</div>
                          <div>4. SLOT_OPENED fires → host is asked for a match</div>
                          <div>5. After 1.5s: BACKFILL_MATCH toast appears bottom-right</div>
                          <div>6. "View waitlist" highlights top candidate in panel</div>
                          <div>7. Pulsing green slot indicates active backfill search</div>
                          <div style={{ marginTop: 4, color: '#64748b' }}>Switch to "no-match" outcome above to see BACKFILL_NO_MATCH response instead.</div>
                          {acuityEnabled && (
                            <div style={{ marginTop: 4, color: '#6ee7b7' }}>
                              Tip: releasing an acuity reservation also triggers slot-opened.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  {/* ── No-Show Risk ── */}
                  <ConfigSection title="No-Show Risk Engine" signal={sectionSignal} bh>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Risk-scores each appointment and schedules smart multi-channel reminders.
                      Badges appear on calendar events; a summary panel shows high/critical counts.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={noShowRiskEnabled}
                        onChange={(e) => setNoShowRiskEnabled(e.target.checked)}
                      />
                      Enable No-Show Risk Engine
                    </label>

                    {noShowRiskEnabled && (
                      <>
                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Risk badge colors:</div>
                          <div><span style={{ color: '#22c55e' }}>●</span> Low — hidden (opacity 0), no badge shown</div>
                          <div><span style={{ color: '#f59e0b' }}>●</span> Moderate — amber chip on event</div>
                          <div><span style={{ color: '#ef4444' }}>●</span> High — red chip + reminder icons</div>
                          <div><span style={{ color: '#dc2626', fontWeight: 700 }}>●</span> Critical — solid red + outreach flag</div>
                        </div>
                        <div style={{ marginTop: 8, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Demo scoring heuristic:</div>
                          <div>• Every 7th appt → <span style={{ color: '#dc2626' }}>critical</span> (92 score)</div>
                          <div>• Every 4th appt → <span style={{ color: '#f59e0b' }}>moderate</span> (52 score)</div>
                          <div>• Early morning (&lt;8am) → <span style={{ color: '#ef4444' }}>high</span> (70 score)</div>
                          <div>• All others → <span style={{ color: '#22c55e' }}>low</span> (15 score)</div>
                          <div style={{ marginTop: 4, color: '#64748b' }}>Risk scores load when view mounts. Tick fires every 5 min to check due reminders.</div>
                        </div>
                        <div style={{ marginTop: 8, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>AI commands (if AI panel enabled):</div>
                          {RISK_PROMPT_EXAMPLES.map((ex) => (
                            <div key={ex}>• "{ex}"</div>
                          ))}
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  {/* ── Group Therapy ── */}
                  <ConfigSection title="Group Therapy" signal={sectionSignal} bh>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Manage group sessions — list open groups, enroll clients, track attendance, and detect scheduling conflicts.
                    </div>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={groupTherapyEnabled}
                        onChange={(e) => setGroupTherapyEnabled(e.target.checked)}
                      />
                      Enable Group Therapy
                    </label>

                    {groupTherapyEnabled && (
                      <>
                        <div style={{ marginTop: 10, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Demo group sessions (this week):</div>
                          <div><span style={{ color: '#7C3AED' }}>●</span> Mon 10–11:30 — DBT Skills (9/12 enrolled, 2 waitlisted)</div>
                          <div><span style={{ color: '#DC2626' }}>●</span> Tue 14–15:30 — Trauma Processing (8/8 full, 3 waitlisted)</div>
                          <div><span style={{ color: '#0891B2' }}>●</span> Wed 9–10 — Anxiety Management (5/10 open)</div>
                          <div><span style={{ color: '#B45309' }}>●</span> Wed 13–14:30 — Substance Use Recovery (11/15 open)</div>
                          <div><span style={{ color: '#059669' }}>●</span> Thu 11–12 — CBT Psychoeducation (4/12 open)</div>
                          <div><span style={{ color: '#9333EA' }}>●</span> Fri 10–11:30 — Grief &amp; Loss Support (8/10 open)</div>
                        </div>
                        <div style={{ marginTop: 8, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>Capacity badge colors:</div>
                          <div><span style={{ color: '#22c55e' }}>●</span> Green — under 50% full</div>
                          <div><span style={{ color: '#eab308' }}>●</span> Yellow — 50–79% full</div>
                          <div><span style={{ color: '#f97316' }}>●</span> Orange — 80–99% full</div>
                          <div><span style={{ color: '#ef4444' }}>●</span> Red — at capacity (waitlist only)</div>
                          <div><span style={{ color: '#94a3b8' }}>●</span> Grey — group closed</div>
                        </div>
                        <div style={{ marginTop: 8, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>How to use:</div>
                          <div>1. Click any group event on the calendar</div>
                          <div>2. The "Groups" tab opens in the right panel</div>
                          <div>3. Click a group to see roster + attendance</div>
                          <div>4. Use "Enroll Patient" to trigger enrollment</div>
                          <div style={{ marginTop: 4, color: '#64748b' }}>Enrollment checks for scheduling conflicts before confirming.</div>
                        </div>
                        <div style={{ marginTop: 8, padding: '8px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                          <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>AI commands (if AI panel enabled):</div>
                          {GROUP_PROMPT_EXAMPLES.map((ex) => (
                            <div key={ex}>• "{ex}"</div>
                          ))}
                        </div>
                      </>
                    )}
                  </ConfigSection>

                  </ConfigGroup>

                  <ConfigGroup title="Appearance">

                  {/* ── Features ── */}
                  <ConfigSection title="Features" signal={sectionSignal}>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={showResources}
                        onChange={(e) => toggleResources(e.target.checked)}
                      />
                      Show Resources (15 team members)
                    </label>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.allowEventCreation ?? false}
                        onChange={(e) => updateConfig('allowEventCreation', e.target.checked)}
                      />
                      Click to Create Events
                    </label>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={useCustomDoubleClickForm}
                        onChange={(e) => setUseCustomDoubleClickForm(e.target.checked)}
                      />
                      Custom Double-Click Form (host-controlled)
                    </label>
                    {useCustomDoubleClickForm && (
                      <div style={{ marginLeft: 20, padding: '6px 10px', background: '#1e293b', borderRadius: 6, fontSize: 10, color: '#94a3b8', lineHeight: 1.6 }}>
                        <div style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}>How this works:</div>
                        <div>Double-clicking an event suppresses the embedded Kendo form.</div>
                        <div>The host app opens its own custom dialog instead.</div>
                        <div style={{ marginTop: 4, color: '#64748b' }}>Toggle off to restore the built-in form.</div>
                      </div>
                    )}
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={!!config.workingHours}
                        onChange={(e) =>
                          updateConfig(
                            'workingHours',
                            e.target.checked
                              ? { start: '08:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] }
                              : undefined,
                          )
                        }
                      />
                      Global Working Hours
                    </label>
                    {config.workingHours && (
                      <div style={{ marginLeft: 20, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div className="ctrl-row">
                          <label>Start</label>
                          <input
                            type="time"
                            className="ctrl-input"
                            value={config.workingHours.start}
                            onChange={(e) =>
                              updateConfig('workingHours', { ...config.workingHours!, start: e.target.value })
                            }
                          />
                        </div>
                        <div className="ctrl-row">
                          <label>End</label>
                          <input
                            type="time"
                            className="ctrl-input"
                            value={config.workingHours.end}
                            onChange={(e) =>
                              updateConfig('workingHours', { ...config.workingHours!, end: e.target.value })
                            }
                          />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 2 }}>
                          {DAYS_OF_WEEK.map(({ label, value: dow }) => {
                            const activeDays = config.workingHours!.daysOfWeek ?? [1, 2, 3, 4, 5];
                            const active = activeDays.includes(dow);
                            return (
                              <label key={dow} className="ctrl-check" style={{ margin: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={active}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? [...activeDays, dow].sort((a, b) => a - b)
                                      : activeDays.filter((d) => d !== dow);
                                    updateConfig('workingHours', { ...config.workingHours!, daysOfWeek: next });
                                  }}
                                />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={(config.backgroundEvents?.length ?? 0) > 0}
                        onChange={(e) =>
                          updateConfig('backgroundEvents', e.target.checked ? getDefaultConfig(false).backgroundEvents : [])
                        }
                      />
                      Background Events
                    </label>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.showFullDay ?? false}
                        onChange={(e) => updateConfig('showFullDay', e.target.checked)}
                      />
                      Show Full Day (24h)
                    </label>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.showAllDaySection ?? true}
                        onChange={(e) => updateConfig('showAllDaySection', e.target.checked)}
                      />
                      Show All Day Section
                    </label>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.showTimezone !== false}
                        onChange={(e) => updateConfig('showTimezone', e.target.checked)}
                      />
                      Show Timezone Picker
                    </label>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={config.showRightTimeColumn ?? false}
                        onChange={(e) => updateConfig('showRightTimeColumn', e.target.checked)}
                      />
                      Show Right Time Column
                    </label>
                  </ConfigSection>

                  {/* ── Toolbar Buttons ── */}
                  <ConfigSection title="Toolbar Buttons" signal={sectionSignal}>
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '4px 8px', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>Button</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>Show</span>
                      {TOOLBAR_BUTTONS.map(({ key, label }) => {
                        const btnCfg = config.toolbarButtons?.[key];
                        const hidden = btnCfg?.hidden ?? false;
                        const updateBtn = (patch: Partial<ToolbarButtonConfig>) =>
                          updateConfig('toolbarButtons', {
                            ...config.toolbarButtons,
                            [key]: { ...btnCfg, ...patch },
                          } as ToolbarButtonsConfig);
                        return (
                          <React.Fragment key={key}>
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>{label}</span>
                            <label className="ctrl-check" style={{ margin: 0 }}>
                              <input
                                type="checkbox"
                                checked={!hidden}
                                onChange={(e) => updateBtn({ hidden: !e.target.checked })}
                              />
                            </label>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </ConfigSection>

                  {/* ── Display / Formatting ── */}
                  <ConfigSection title="Header Text" signal={sectionSignal}>
                    <div className="ctrl-row">
                      <label>Text</label>
                      <input
                        type="text"
                        className="ctrl-input"
                        placeholder="e.g. Team Schedule"
                        value={config.headerText ?? ''}
                        onChange={(e) => updateConfig('headerText', e.target.value || undefined)}
                      />
                    </div>
                  </ConfigSection>

                  <ConfigSection title="Header Date Formats" signal={sectionSignal}>
                    {(['day', 'weekdays', 'fullWeek', 'month', 'agenda'] as ViewType[]).map((v) => (
                      <div key={v} className="ctrl-row">
                        <label>{VIEW_LABELS[v]}</label>
                        <select
                          className="ctrl-select"
                          value={formatIndexes[v]}
                          onChange={(e) => updateDateFormat(v, parseInt(e.target.value))}
                        >
                          {VIEW_FORMAT_OPTIONS[v].map((opt, i) => (
                            <option key={i} value={i}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </ConfigSection>

                  <ConfigSection title="Column Header Formats" signal={sectionSignal}>
                    {(['day', 'weekdays', 'fullWeek', 'month'] as ViewType[]).map((v) => (
                      <div key={v} className="ctrl-row">
                        <label>{VIEW_LABELS[v]}</label>
                        <select
                          className="ctrl-select"
                          value={colHeaderIndexes[v]}
                          onChange={(e) => updateColHeaderFormat(v, parseInt(e.target.value))}
                        >
                          {COL_HEADER_PRESETS[v].map((opt, i) => (
                            <option key={i} value={i}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </ConfigSection>

                  <ConfigSection title="All Day Section" signal={sectionSignal}>
                    <div className="ctrl-row">
                      <label>Label</label>
                      <input
                        type="text"
                        className="ctrl-input"
                        placeholder="All day"
                        value={config.allDayLabel ?? ''}
                        onChange={(e) => updateConfig('allDayLabel', e.target.value || undefined)}
                      />
                    </div>
                  </ConfigSection>

                  <ConfigSection title="Event Icons" signal={sectionSignal}>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={showEventIcons}
                        onChange={(e) => setShowEventIcons(e.target.checked)}
                      />
                      Priority Icon (bottom-right)
                    </label>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>
                      🔴 Critical &nbsp; 🟠 High &nbsp; 🟡 Medium &nbsp; 🔵 Low
                    </div>
                  </ConfigSection>

                  </ConfigGroup>

                  <ConfigGroup title="Data">

                  {/* ── Calendar Content ── */}
                  <ConfigSection title="Holidays" signal={sectionSignal}>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={showHolidays}
                        onChange={(e) => setShowHolidays(e.target.checked)}
                      />
                      US Federal Holidays (2026)
                    </label>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>
                      Read-only all-day events — not editable by end users.
                    </div>

                    {/* Custom holiday form */}
                    <div style={{ marginTop: 8, borderTop: '1px solid #334155', paddingTop: 8 }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>
                        Add Custom Holiday
                      </div>
                      <div className="ctrl-row" style={{ marginBottom: 4 }}>
                        <label>Name</label>
                        <input
                          type="text"
                          className="ctrl-input"
                          placeholder="e.g. Company Day Off"
                          value={newHolidayName}
                          onChange={(e) => setNewHolidayName(e.target.value)}
                        />
                      </div>
                      <div className="ctrl-row" style={{ marginBottom: 4 }}>
                        <label>Date</label>
                        <input
                          type="date"
                          className="ctrl-input"
                          value={newHolidayDate}
                          onChange={(e) => setNewHolidayDate(e.target.value)}
                        />
                      </div>
                      <div className="ctrl-row" style={{ marginBottom: 4 }}>
                        <label>Color</label>
                        <input
                          type="color"
                          style={{ width: 36, height: 28, padding: 2, border: '1px solid #334155', borderRadius: 4, background: 'transparent', cursor: 'pointer' }}
                          value={newHolidayColor}
                          onChange={(e) => setNewHolidayColor(e.target.value)}
                        />
                      </div>
                      <label className="ctrl-check" style={{ marginBottom: 6 }}>
                        <input
                          type="checkbox"
                          checked={newHolidayRecurring}
                          onChange={(e) => setNewHolidayRecurring(e.target.checked)}
                        />
                        Repeat annually
                      </label>
                      <button
                        className="ctrl-btn"
                        style={{ width: '100%' }}
                        disabled={!newHolidayName.trim() || !newHolidayDate}
                        onClick={() => {
                          if (!newHolidayName.trim() || !newHolidayDate) return;
                          setCustomHolidays((prev) => [
                            ...prev,
                            { name: newHolidayName.trim(), date: newHolidayDate, color: newHolidayColor, recurring: newHolidayRecurring },
                          ]);
                          setNewHolidayName('');
                          setNewHolidayDate('');
                          setNewHolidayColor('#6366f1');
                          setNewHolidayRecurring(false);
                        }}
                      >
                        + Add Holiday
                      </button>
                    </div>

                    {/* Custom holiday list */}
                    {customHolidays.length > 0 && (
                      <div style={{ marginTop: 8, borderTop: '1px solid #334155', paddingTop: 8 }}>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, fontWeight: 600 }}>
                          Custom Holidays
                        </div>
                        {customHolidays.map((h, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{
                              display: 'inline-block', width: 8, height: 8,
                              borderRadius: '50%', background: h.color ?? '#6366f1', flexShrink: 0,
                            }} />
                            <span style={{ fontSize: 11, color: '#cbd5e1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {h.name} · {h.date}{h.recurring ? ' (yearly)' : ''}
                            </span>
                            <button
                              className="ctrl-btn ctrl-btn-ghost"
                              style={{ padding: '1px 6px', fontSize: 11, flexShrink: 0, width: 'auto' }}
                              onClick={() => setCustomHolidays((prev) => prev.filter((_, j) => j !== i))}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </ConfigSection>

                  {/* ── Integration & Data ── */}
                  <ConfigSection title="Live Updates (SSE)" signal={sectionSignal}>
                    <label className="ctrl-check">
                      <input
                        type="checkbox"
                        checked={sseEnabled}
                        onChange={(e) => setSseEnabled(e.target.checked)}
                      />
                      Enable SSE connection
                    </label>
                    <div className="ctrl-row">
                      <label>Server URL</label>
                      <input
                        type="text"
                        className="ctrl-input"
                        value={sseUrl}
                        onChange={(e) => setSseUrl(e.target.value)}
                        placeholder="http://localhost:3001/events"
                        disabled={sseEnabled}
                      />
                    </div>
                    {sseEnabled && (
                      <div className="ctrl-row">
                        <label>Status</label>
                        <span style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: {
                            connected: '#22c55e',
                            connecting: '#f59e0b',
                            disconnected: '#94a3b8',
                            error: '#ef4444',
                          }[sseStatus],
                        }}>
                          {sseStatus}
                        </span>
                      </div>
                    )}
                    <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0 0' }}>
                      Start the demo server: <code>npm run dev:sse</code>
                    </p>
                  </ConfigSection>

                  <ConfigSection title="Data" signal={sectionSignal}>
                    <button
                      className="ctrl-btn"
                      onClick={() => setEvents(generateEvents(config.timezone))}
                    >
                      Reload Sample Events
                    </button>
                    <button
                      className="ctrl-btn ctrl-btn-ghost"
                      onClick={() => setEvents([])}
                    >
                      Clear All Events
                    </button>
                  </ConfigSection>

                  {/* ── Validation ── */}
                  <ConfigSection title="Validation Rules" signal={sectionSignal}>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                      Block user actions that violate these rules. Blocked actions show a brief error toast inside the scheduler and are logged here as VALIDATION_ERROR.
                    </div>

                    <label className="ctrl-check" title="Prevents clicking empty slots or saving the create form for times before now">
                      <input
                        type="checkbox"
                        checked={validation.preventPastEventCreation ?? false}
                        onChange={(e) => setValidation((v: ScheduleValidation) => ({ ...v, preventPastEventCreation: e.target.checked }))}
                      />
                      Block creating events in the past
                    </label>

                    <label className="ctrl-check" title="Prevents dragging or resizing events whose original start time has already passed">
                      <input
                        type="checkbox"
                        checked={validation.preventPastEventEdits ?? false}
                        onChange={(e) => setValidation((v: ScheduleValidation) => ({ ...v, preventPastEventEdits: e.target.checked }))}
                      />
                      Block editing / moving past events
                    </label>

                    <label className="ctrl-check" title="Prevents deleting events whose end time is in the past">
                      <input
                        type="checkbox"
                        checked={validation.preventPastEventDeletion ?? false}
                        onChange={(e) => setValidation((v: ScheduleValidation) => ({ ...v, preventPastEventDeletion: e.target.checked }))}
                      />
                      Block deleting past events
                    </label>

                    <label className="ctrl-check" title="Prevents creating new events outside the resource's (or global) working hours">
                      <input
                        type="checkbox"
                        checked={validation.preventCreationOutsideWorkingHours ?? false}
                        onChange={(e) => setValidation((v: ScheduleValidation) => ({ ...v, preventCreationOutsideWorkingHours: e.target.checked }))}
                      />
                      Block creation outside working hours
                    </label>

                    <label className="ctrl-check" title="Prevents dragging or resizing events to slots outside the resource's (or global) working hours">
                      <input
                        type="checkbox"
                        checked={validation.preventMoveOutsideWorkingHours ?? false}
                        onChange={(e) => setValidation((v: ScheduleValidation) => ({ ...v, preventMoveOutsideWorkingHours: e.target.checked }))}
                      />
                      Block drag / resize outside working hours
                    </label>

                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                      Working-hours rules require <strong>Working Hours</strong> to be configured above (global or per resource).
                    </div>
                  </ConfigSection>

                  </ConfigGroup>
                </div>
              )}

              {sidebarTab === 'log' && (
                <div className="log-panel" ref={logRef}>
                  <div className="log-toolbar">
                    <span className="log-count">{log.length} events</span>
                    <button className="log-clear" onClick={() => { setLog([]); setExpandedLogIds(new Set()); }}>Clear</button>
                  </div>
                  {log.length === 0 ? (
                    <div className="log-empty">No events yet. Interact with the calendar.</div>
                  ) : (
                    log.map((entry) => {
                      const isExpanded = expandedLogIds.has(entry.id);
                      const hasPayload = entry.payload !== undefined;
                      return (
                        <div
                          key={entry.id}
                          className={`log-entry${hasPayload ? ' log-entry--expandable' : ''}${isExpanded ? ' log-entry--expanded' : ''}`}
                          onClick={hasPayload ? () => setExpandedLogIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(entry.id)) next.delete(entry.id);
                            else next.add(entry.id);
                            return next;
                          }) : undefined}
                        >
                          <span className="log-time">{entry.time}</span>
                          <span
                            className="log-type"
                            style={{ color: LOG_TYPE_COLORS[entry.type] ?? '#94a3b8' }}
                          >
                            {entry.type}
                          </span>
                          <span className="log-detail">
                            {entry.detail}
                            {hasPayload && <span className="log-expand-icon">{isExpanded ? '▼' : '▶'}</span>}
                          </span>
                          {isExpanded && (
                            <pre className="log-payload">{JSON.stringify(entry.payload, null, 2)}</pre>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              <div className="sidebar-resize-handle" onMouseDown={handleResizeStart} />
            </>
          )}
        </aside>

        {/* Host-controlled custom double-click form — edit existing event */}
        {customFormEvent && (
          <HostCustomFormModal
            event={customFormEvent}
            resources={resources}
            timezone={liveTimezone}
            observeDaylightSaving={config.observeDaylightSaving}
            onSave={(updated) => {
              addLog('EVENT_SAVED', `"${updated.title}" (custom form)`, updated);
              setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
              setCustomFormEvent(null);
            }}
            onDelete={(eventId) => {
              addLog('EVENT_DELETED', eventId, { eventId });
              setEvents((prev) => prev.filter((e) => e.id !== eventId));
              setCustomFormEvent(null);
            }}
            onClose={() => setCustomFormEvent(null)}
          />
        )}

        {/* Host-controlled custom double-click form — create new event */}
        {newEventSlot && (
          <HostCustomFormModal
            slot={newEventSlot}
            resources={resources}
            timezone={liveTimezone}
            observeDaylightSaving={config.observeDaylightSaving}
            onSave={(newEvent) => {
              addLog('EVENT_CREATED', `"${newEvent.title}" (custom form)`, newEvent);
              setEvents((prev) => [...prev, newEvent]);
              setNewEventSlot(null);
            }}
            onClose={() => setNewEventSlot(null)}
          />
        )}

        {/* Calendar */}
        <main className="calendar-area" style={{ position: 'relative' }}>
          {isLoadingIndustry && (
            <div className="industry-loading-overlay">
              <div className="industry-loading-box">
                <span className="industry-loading-spinner" />
                <span>Loading industry data…</span>
              </div>
            </div>
          )}
          <ScheduleCalendar
            config={effectiveConfig}
            events={effectiveEvents}
            callbacks={callbacks}
            connectionStatus={sseEnabled ? sseStatus : undefined}
            screenerGate={screenerGate}
            privilegedView={acuityPrivilegedView}
            providerMatching={providerMatchingConfig}
            patientContext={activePatientContext}
            waitlist={waitlistConfig}
            noShowRisk={noShowRiskConfig}
            groupTherapy={groupTherapyConfig}
            style={{ height: '100%' }}
          />
        </main>
      </div>
    </div>
  );
}

// ─── Host-controlled custom double-click form ─────────────────────────────
// This modal is rendered by the HOST application (not the embedded scheduler).
// It intentionally uses a light-theme card design to contrast with the embedded
// dark-theme Kendo form — demonstrating that the host has full control over the UI.

interface HostCustomFormModalProps {
  /** Provide when editing an existing event. */
  event?: ScheduleEvent;
  /** Provide when creating a new event from an empty slot. */
  slot?: { startUtc: string; endUtc: string; resourceId?: string };
  resources: Resource[];
  /** The scheduler's configured IANA timezone — times are displayed in this zone. */
  timezone: string;
  /** Whether DST is observed; passed through to resolveTimezone internally. */
  observeDaylightSaving?: boolean;
  onSave: (event: ScheduleEvent) => void;
  onDelete?: (eventId: string) => void;
  onClose: () => void;
}

function HostCustomFormModal({ event, slot, resources, timezone, observeDaylightSaving, onSave, onDelete, onClose }: HostCustomFormModalProps) {
  const isNew = !event;
  const resourceId = event?.resourceId ?? slot?.resourceId;

  const toLocal = (utcIso: string) => utcToScheduleLocal(utcIso, timezone, observeDaylightSaving);
  const toUtc   = (local: string)  => scheduleLocalToUtc(local, timezone, observeDaylightSaving);

  const [title, setTitle] = React.useState(event?.title ?? '');
  const [description, setDescription] = React.useState(event?.description ?? '');
  const [startLocal, setStartLocal] = React.useState(toLocal(event?.startUtc ?? slot?.startUtc ?? ''));
  const [endLocal, setEndLocal] = React.useState(toLocal(event?.endUtc ?? slot?.endUtc ?? ''));
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const resource = resources.find((r) => r.id === resourceId);

  const handleSave = () => {
    const startUtc = toUtc(startLocal);
    const endUtc   = toUtc(endLocal);
    if (isNew) {
      onSave({
        id: crypto.randomUUID(),
        title: title.trim() || 'New Event',
        startUtc,
        endUtc,
        resourceId,
        description: description || undefined,
      });
    } else {
      onSave({ ...event!, title: title.trim() || event!.title, startUtc, endUtc, description: description || undefined });
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        width: 480, maxWidth: '90vw', fontFamily: 'system-ui, sans-serif', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: isNew
            ? 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)'
            : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
          padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
              {isNew ? 'New Appointment' : 'Edit Appointment'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 2 }}>
              Host-controlled form — not the embedded Kendo UI
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
              color: '#fff', cursor: 'pointer', fontSize: 16, padding: '4px 8px', lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Title */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Title
            </label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isNew ? 'New Event' : ''}
              style={{
                width: '100%', boxSizing: 'border-box',
                border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '8px 10px',
                fontSize: 14, color: '#0f172a', outline: 'none',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
            />
          </div>

          {/* Time row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Start
              </label>
              <input
                type="datetime-local"
                value={startLocal}
                onChange={(e) => setStartLocal(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 10px',
                  fontSize: 13, color: '#0f172a', outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                End
              </label>
              <input
                type="datetime-local"
                value={endLocal}
                onChange={(e) => setEndLocal(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 10px',
                  fontSize: 13, color: '#0f172a', outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>
          </div>

          {/* Resource */}
          {resource && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Resource</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                  background: resource.color ?? '#3b82f6', flexShrink: 0,
                }} />
                <span style={{ fontSize: 13, color: '#1e293b' }}>{resource.name}</span>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Add a description…"
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'vertical',
                border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '8px 10px',
                fontSize: 13, color: '#0f172a', outline: 'none', fontFamily: 'inherit',
                transition: 'border-color 0.15s',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
            />
          </div>

          {/* Custom attributes (read-only display, edit mode only) */}
          {event?.customAttributes && event.customAttributes.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {event.customAttributes.map((attr) => (
                  <div key={attr.key} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                    <span style={{ color: '#94a3b8', minWidth: 80 }}>{attr.key}</span>
                    <span style={{ color: '#334155' }}>{String(attr.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {/* Delete side (edit mode only) */}
          <div>
            {!isNew && onDelete && (
              !confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{
                    background: 'none', border: '1.5px solid #fca5a5', borderRadius: 7,
                    color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    padding: '6px 12px',
                  }}
                >
                  Delete
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#ef4444' }}>Are you sure?</span>
                  <button
                    onClick={() => onDelete(event!.id)}
                    style={{
                      background: '#ef4444', border: 'none', borderRadius: 7,
                      color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                      padding: '5px 10px',
                    }}
                  >
                    Yes, delete
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    style={{
                      background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 7,
                      color: '#64748b', cursor: 'pointer', fontSize: 11, padding: '5px 10px',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )
            )}
          </div>

          {/* Save / Close */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 7,
                color: '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                padding: '7px 16px',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                background: isNew
                  ? 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                border: 'none', borderRadius: 7,
                color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                padding: '7px 18px', boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
              }}
            >
              {isNew ? 'Create event' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigGroup({ title, children, bh }: { title: string; children: React.ReactNode; bh?: boolean }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={`config-group${bh ? ' config-group--bh' : ''}`}>
      <button
        className="config-group-header"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <span className="config-group-title">{title}</span>
        <span className="config-group-rule" />
        <span className={`config-group-chevron${collapsed ? '' : ' expanded'}`}>›</span>
      </button>
      {!collapsed && children}
    </div>
  );
}

function ConfigSection({ title, children, defaultCollapsed = true, signal, bh }: { title: string; children: React.ReactNode; defaultCollapsed?: boolean; signal?: { collapsed: boolean } | null; bh?: boolean }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  React.useEffect(() => {
    if (signal != null) setCollapsed(signal.collapsed);
  }, [signal]);

  return (
    <div className={`config-section${bh ? ' config-section--bh' : ''}`}>
      <button
        className="config-section-header"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="config-section-title">{title}</span>
          {bh && <span className="config-section-badge config-section-badge-bh">BH</span>}
        </span>
        <span className={`config-section-chevron${collapsed ? '' : ' expanded'}`}>›</span>
      </button>
      {!collapsed && (
        <div className="config-section-body">{children}</div>
      )}
    </div>
  );
}
